import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CountrySelectWrapper from "@/_components/CountrySelectWrapper"
import CustomButton from "@/_components/CustomButton"
import CustomCheckbox from "@/_components/CustomCheckbox"
import CustomRadio from "@/_components/CustomRadio"
import DebugPopover from "@/_components/DebugPopover"
import FormInputDatetime from "@/_components/FormInputDatetime"
import FormInputInline from "@/_components/FormInputInline"
import HistoryModal from "@/_components/HistoryModal"
import ImageZoomModal, { getZoomedImage } from "@/_components/ImageZoomModal"
import InputValidationComponent from "@/_components/InputValidationComponent"
import Slider from "@/_components/Slider"
import { getCachedCurrenciesMap } from "@/_services/coreUtils"
import { getFileNameExtension, getFileNameWithoutExtension } from "@/_services/fileUtils"
import { hasFieldPropsChanged, isEmpty, syncFieldProps, toPatch } from "@/_services/inputUtils"
import { getRichValues, getValues } from "@/_services/lists"
import { getLocale, loc } from "@/_services/localization"
import {
  deleteTemporaryOfflineDocument,
  downloadTemporaryOfflineDocument,
  hasOfflineDocumentUpload,
  isOffline,
  storeTemporaryOfflineDocument,
} from "@/_services/offlineService"
import { getConfigAtPath, getOptions, getTenant, getTimeZone, getUseOptimizedFormInput, getUserConfiguration } from "@/_services/userConfiguration"
import {
  applyClasses,
  applySelectOptions,
  checkIfInIframe,
  checkRegistrationFormat,
  copyToClipboard,
  customEvents,
  domAttributes,
  execKatex,
  execMermaid,
  fileToBase64,
  formatCurrency,
  formatDate,
  formatDateTime,
  getMermaidIds,
  getNavigationRef,
  handleAccessibleOnKeyDown,
  internalDebounce,
  isPromise,
  labelFromName,
  onDropdownMenuButtonKeyDown,
  onDropdownToggleKeyDown,
  openDocInTab,
  selectNativeStyles,
  setBeforeUnloadListener,
} from "@/_services/utils"
import { setFormInputChangeHandler as setVirtualKeyboardChangeHandler } from "@/_services/virtual-keyboard"
import Datetime from "@basikon2/react-datetime"
import axios from "axios"
import {
  isValidDate as commonUtilsIsValidDate,
  finance,
  formatDecimal,
  formatIban,
  formatPercentage,
  getCurrencySymbol,
  getDecimalSymbol,
  getEntityName,
  getEntityServerRoute,
  getFieldProps,
  getFieldPropsPath,
  getNsnLengthByCallingCode,
  getPhoneLengthRegExp,
  isGreaterThan,
  isGreaterThanMax,
  isGreaterThanOrEqual,
  isLessThanMin,
  isObject,
  isString,
  isValidUsername,
  momentDateFormat,
  parseBoolean,
  removeAllAccents,
  replaceUrlParams,
  setFieldProps,
  unformatCurrency,
  unformatIban,
  validationRegExps,
} from "basikon-common-utils"
import DOMPurify from "dompurify"
import deepClone from "lodash.clonedeep"
import debounce from "lodash.debounce"
import get from "lodash.get"
import merge from "lodash.merge"
import { marked } from "marked"
import moment from "moment-timezone"
import React, { Suspense, createRef } from "react"
import { ButtonGroup, Col, ControlLabel, Dropdown, FormControl, FormGroup, InputGroup, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import { Link } from "react-router-dom"
import Select, { components } from "react-select"
import AsyncSelect from "react-select/async"
import AsyncCreatableSelect from "react-select/async-creatable"
import CreatableSelect from "react-select/creatable"
import { v4 as uuidv4 } from "uuid"

const { sanitize } = DOMPurify

const FormInputRichText = React.lazy(() => import("@/_components/FormInputRichText"))
const Switch = React.lazy(() => import("@/_components/BootstrapSwitch"))
const { phoneRegExp, emailRegExp } = validationRegExps

const actionTypes = {
  SELECT_FILE: "selectFile",
  VIEW_FILE: "viewFile",
  DELETE_FILE: "deleteFile",
  EDIT_FILE_NAME: "editFileName",
}

const dataDropdownIsOpen = "data-dropdown-is-open"

class FormInput extends React.Component {
  constructor(props) {
    super(props)

    this.ownRef = React.createRef()

    const { field, modelPath = "", defaultRenderMarkdown } = props
    const _modelPath = modelPath ? modelPath + "." : ""
    const modelFieldPath = _modelPath + field

    let entityName
    let entityRegistration
    const clientRoute = window?.location?.pathname // ex: "/asset/GLI00281891"
    if (clientRoute && !clientRoute.endsWith("/") && clientRoute.split("/").length - 1 === 2) {
      const clientRouteElements = clientRoute.split("/")
      if (clientRouteElements.length - 1 === 2) {
        entityName = getEntityName({ clientRoute: `/${clientRouteElements[1]}` }) // ex: "/asset/GLI00281891" => "/asset" => "Asset"
        entityRegistration = clientRouteElements[2] // ex: "/asset/GLI00281891" => "GLI00281891"
      }
    }

    this.state = {
      keyedValue: null,
      modelFieldPath,
      fieldPropsPath: getFieldPropsPath(modelFieldPath),
      debug: false,
      selectListIsLoaded: false,
      showDropBox: false,
      navigationRef: getNavigationRef(),
      // Type "markdown": the content is rendered by default, "defaultRenderMarkdown: false" starts in edition mode
      renderMarkdown: defaultRenderMarkdown ?? true,
      // Some browsers like Chrome override the "off" autocomplete value.
      // So we need to provide a different value that does the same but that browsers don't override,
      // but still keep a standard value, otherwise accessibility tests don't pass.
      defaultAutoComplete: getOptions("browserAutofill") ? undefined : "one-time-code",
      forceComponentUpdate: null,
      useOptimizedFormInput: getUseOptimizedFormInput(),
      showHistoryModal: false,
      entityName,
      entityRegistration,
      mentionStart: null,
      mentionPersons: [],
      mentionQuery: null,
      showMentions: false,
      mentionIndex: 0,
      showFormInputValueInModal: false,
      showRawMarkdown: false,
      zoomedImage: null,
    }
    this.dragCounter = 0
    this.textareaRef = createRef()
    this.mentionItemRefs = []

    if (props.onSetState) {
      const debounceWait = this.getDebounceWait(props.debounce)
      if (debounceWait) this.onSetStateDebounced = debounce(props.onSetState, debounceWait, props.debounceOptions).bind(this)
    }

    this.hasCalledShouldUpdate = null
  }

  componentDidMount() {
    const { obj, field, mandatory, select, min, max, type } = this.props

    if (["radio", "checkbox"].includes(type)) this.setState({ numberId: uuidv4() })

    if (isPromise(select)) select.then(selectOptions => this.setState({ selectOptions }))
    syncFieldProps({ obj, field, mandatory, min, max, type })
    window.addEventListener(customEvents.formInput.forceComponentUpdate, this.forceComponentUpdate)
    window.addEventListener(customEvents.formInput.enableDebug, this.enableDebug)
  }

  enableDebug = event => {
    const { detail } = event
    this.setState({ debug: detail })
  }

  forceComponentUpdate = event => {
    const { useOptimizedFormInput, modelFieldPath, fieldPropsPath } = this.state
    if (!useOptimizedFormInput) return true

    const { detail } = event
    const { errors, fieldProps, force, reloadLists } = detail
    const statePatch = { forceComponentUpdate: Date.now() }
    if (reloadLists) statePatch.selectListIsLoaded = false

    if (force) {
      this.setState(statePatch)
      return
    }

    if (errors) {
      for (const error of errors) {
        if (error.modelFieldPath === modelFieldPath) {
          this.setState(statePatch)
          return
        }
      }
    }

    if (fieldProps) {
      for (const key in fieldProps) {
        if (key === fieldPropsPath) {
          this.setState(statePatch)
          return
        }
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { obj, field, mandatory, min, max, select, type, formInputProps } = this.props
    const { mandatory: prevMandatory, min: prevMin, max: prevMax, select: prevSelect, type: prevType, formInputProps: prevFormInputProps } = prevProps
    const args = { obj, field, mandatory, min, max, type, prevMandatory, prevType }
    if (mandatory !== prevMandatory || min !== prevMin || max !== prevMax || type !== prevType || hasFieldPropsChanged(args)) {
      syncFieldProps(args)
      if (!type && prevType) this.forceUpdate()
    }

    const currentSelect = select ?? formInputProps?.select
    const previousSelect = prevSelect ?? prevFormInputProps?.select
    if (typeof currentSelect === "string" && currentSelect !== previousSelect) {
      this.setState({ selectListIsLoaded: false })
    }
  }

  componentWillUnmount() {
    const { obj, field } = this.props
    // syncFieldProps is broken in this context when useOptimizedFormInput is enabled because the layout is rendered on an entity
    // clone, and so, the obj is a broken reference to the actual entity. This is a breaking change detected after two years, but
    // because it is not affecting current customers and new customers write their configs with the option enabled, there is no
    // stake in prod.
    syncFieldProps({ obj, field, mandatory: false, min: null, max: null })
    window.removeEventListener(customEvents.formInput.forceComponentUpdate, this.forceComponentUpdate)
    window.removeEventListener(customEvents.formInput.enableDebug, this.enableDebug)
  }

  /**
   * The most costly operation in the app is the rendering of the form inputs
   * when there are many, which is the case in some pages like and deal and contract.
   * To avoid unecessary renders, we finely decide when to update them or not.
   */
  shouldComponentUpdate(nextProps, nextState) {
    const {
      // modelFieldPath, // for dev debug
      useOptimizedFormInput,
      keyedValue,
      selectListIsLoaded,
      numberId,
      selectOptions,
      renderMarkdown,
      showFileNameInput,
      originalFileName,
      editableFileName,
      showDropBox,
      warningFormat: stateWarningFormat,
      forceComponentUpdate,
      debug,
      showHistoryModal,
      showMentions,
      mentionIndex,
      showFormInputValueInModal,
      showRawMarkdown,
      zoomedImage,
    } = this.state

    if (!useOptimizedFormInput) return true

    const {
      keyedValue: nextKeyValue,
      selectListIsLoaded: nextSelectListIsLoaded,
      numberId: nextNumberId,
      selectOptions: nextSelectOptions,
      renderMarkdown: nextRenderMarkdown,
      showFileNameInput: nextShowFileNameInput,
      originalFileName: nextOriginalFileName,
      editableFileName: nextEditableFileName,
      showDropBox: nextShowDropBox,
      warningFormat: nextStateWarningFormat,
      forceComponentUpdate: nextForceComponentUpdate,
      debug: nextDebug,
      showHistoryModal: nextShowHistoryModal,
      showMentions: nextShowMentions,
      mentionIndex: nextMentionIndex,
      showFormInputValueInModal: nextShowFormInputValueInModal,
      showRawMarkdown: nextShowRawMarkdown,
      zoomedImage: nextZoomedImage,
    } = nextState

    const {
      type,
      value,
      obj,
      field,
      disabled,
      readOnly,
      isLoading,
      select,
      hidden,
      label,
      renderMarkdown: propsRenderMarkdown,
      mandatory,
      min,
      max,
      errorFormat: propsErrorFormat,
      fcClassName,
      personsToMention,
      helper,
      actionIcon,
      actionClassName,
      actionTooltip,
    } = this.getAllProps()
    const {
      type: nextType,
      value: nextValue,
      obj: nextObj,
      field: nextField,
      disabled: nextDisabled,
      readOnly: nextReadOnly,
      isLoading: nextIsLoading,
      select: nextSelect,
      hidden: nextHidden,
      label: nextLabel,
      renderMarkdown: nextPropsRenderMarkdown,
      mandatory: nextMandatory,
      min: nextMin,
      max: nextMax,
      errorFormat: nextPropsErrorFormat,
      fcClassName: nextFcClassName,
      personsToMention: nextPersonsToMention,
      helper: nextHelper,
      actionIcon: nextActionIcon,
      actionClassName: nextActionClassName,
      actionTooltip: nextActionTooltip,
    } = this.getAllProps(nextProps)

    // Checks must be ordered from less to most costly to compare
    // hasCalledShouldUpdate check necessary because react 18 render faster than a possible default value set state
    let result =
      !this.hasCalledShouldUpdate ||
      min !== nextMin ||
      max !== nextMax ||
      label !== nextLabel ||
      debug !== nextDebug ||
      showHistoryModal !== nextShowHistoryModal ||
      showMentions !== nextShowMentions ||
      mentionIndex !== nextMentionIndex ||
      forceComponentUpdate !== nextForceComponentUpdate ||
      hidden !== nextHidden ||
      keyedValue !== nextKeyValue ||
      selectListIsLoaded !== nextSelectListIsLoaded ||
      disabled !== nextDisabled ||
      numberId !== nextNumberId ||
      selectOptions !== nextSelectOptions ||
      readOnly !== nextReadOnly ||
      renderMarkdown !== nextRenderMarkdown ||
      propsRenderMarkdown !== nextPropsRenderMarkdown ||
      showFileNameInput !== nextShowFileNameInput ||
      originalFileName !== nextOriginalFileName ||
      editableFileName !== nextEditableFileName ||
      showDropBox !== nextShowDropBox ||
      propsErrorFormat !== nextPropsErrorFormat ||
      stateWarningFormat !== nextStateWarningFormat ||
      mandatory !== nextMandatory ||
      helper !== nextHelper ||
      actionIcon !== nextActionIcon ||
      actionClassName !== nextActionClassName ||
      actionTooltip !== nextActionTooltip ||
      isLoading !== nextIsLoading ||
      fcClassName !== nextFcClassName ||
      personsToMention !== nextPersonsToMention ||
      showFormInputValueInModal !== nextShowFormInputValueInModal ||
      showRawMarkdown !== nextShowRawMarkdown ||
      zoomedImage !== nextZoomedImage ||
      obj?._id !== nextObj?._id ||
      obj?.registration !== nextObj?.registration ||
      // The type change is quite rare, it happens notably in TableExtended when add the expanded col
      // which unshifts cols and thus their type.
      type !== nextType ||
      this.getParsedValue({ value, field, obj }) !== this.getParsedValue({ value: nextValue, field: nextField, obj: nextObj })

    if (!result) {
      // This is not the most efficient way of comparing arrays but select arrays are rare and usually small.
      // This operation can fail if the select contains circular refs, so we wrap it in a try catch.
      if (Array.isArray(select) && Array.isArray(nextSelect)) {
        try {
          result = JSON.stringify(select) !== JSON.stringify(nextSelect)
        } catch (error) {
          console.log("Error comparing select arrays in FormInput shouldComponentUpdate:", field, select, error)
        }
      } else {
        result = select !== nextSelect
      }
    }

    this.hasCalledShouldUpdate = true

    return result
  }

  handleChange = async event => {
    const {
      field: fieldName,
      label,
      type,
      timeFormat,
      select,
      noLodash,
      negative,
      upperCase,
      lowerCase,
      obj,
      regExp,
      fileType,
      onSelect,
      mandatory,
      onSetState,
      debounceWait,
      isMulti,
    } = this.getAllProps()
    const { keyedValue: stateKeyedValue } = this.state

    const textLabel = !label && label !== "" ? labelFromName(fieldName) : loc(label)
    const parsedType = (type ? type : "text")?.toLowerCase()
    let patch = {}
    let value
    let errorFormat = null
    let keyedValue = event?.target?.value

    const locale = getLocale()
    const hasToBeNumberMessage = loc`${textLabel} has to be a number`
    const numberInputRegex = /^-?\s?(?:\d*\.?\d*|\d+)(?:e[-+]?\d*)?$|^(?:\d*\.?\d*|\d+)(?:e[-+]?\d*)?$/i

    if (["date", "datetime"].includes(parsedType)) {
      if (typeof event === "string") {
        keyedValue = event // not an event but a string
        value = null
        if (event !== "") errorFormat = loc`Invalid date`
      } else {
        const localDate = event ? event.toDate() : null // not an event but a moment
        if (!getConfigAtPath("useDateNoTime") || timeFormat || parsedType === "datetime") {
          value = localDate
        } else {
          // localDate is currently set at 0h0m0s, on current timezone "X".
          // The next line will change it to Xh0m0s on timezone X, which is equivalent to the UTC value
          value = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()))
        }
      }
    } else if (select && parsedType !== "emails") {
      // not an event but a {value, label}
      value = event ? (Array.isArray(event) && isMulti ? event.map(({ value }) => value) : event.value) : null
      // prevents validation error flash when selecting an option
      keyedValue = value
      if (parsedType && value !== null && value !== undefined) {
        if (parsedType === "number") {
          const digitRex = /^\d+$/
          if (!digitRex.test(value)) {
            errorFormat = hasToBeNumberMessage
          }
        } else {
          errorFormat = checkRegistrationFormat(parsedType, value)
        }
      }
      if (event && event.data) {
        for (const key of Object.keys(event.data)) {
          patch[key] = event.data[key]
        }
      }
      // Set this flag in patch when clicking on the new "creatable" option
      if (event?.__isNew__) patch._isNew = true
    } else if (parsedType === "number" || parsedType === "integer") {
      const hasToBeIntegerMessage = loc`${textLabel} has to be an integer`
      value = keyedValue || keyedValue === 0 ? unformatCurrency(keyedValue, locale) : keyedValue
      if (value === "") value = null
      if (negative) value = -value
      if (isNaN(value)) {
        const isValidNumberInput = numberInputRegex.test(keyedValue)

        if (!isValidNumberInput) {
          const prevValue = unformatCurrency(stateKeyedValue, locale)
          if (!isNaN(prevValue) && prevValue !== null) {
            value = prevValue
            keyedValue = prevValue
          } else {
            return
          }
        }

        if (keyedValue && isNaN(keyedValue)) errorFormat = parsedType === "integer" ? hasToBeIntegerMessage : hasToBeNumberMessage
      } else if (parsedType === "integer" && !Number.isInteger(value) && value !== null) {
        errorFormat = hasToBeIntegerMessage
      }
    } else if (parsedType === "percentage") {
      const trimmedKeyValue = keyedValue?.endsWith("%") ? (keyedValue = keyedValue.substring(0, keyedValue.length - 1).trimRight()) : keyedValue

      let cleanedValue = String(trimmedKeyValue || "")
        .trim()
        .replace(/\s/g, "")

      const digitRex = /^[+-]?([0-9]+([.,][0-9]*)?|[.,][0-9]+)$/
      // a lone sign or decimal separator is the transient state when typing a number [PRD-2385]
      const isValidNumberInput = digitRex.test(cleanedValue) || /^[+-]?[.,]?$/.test(cleanedValue)

      value = (keyedValue || keyedValue === 0) && isValidNumberInput ? finance.ROUND(this.intlParseFloat(cleanedValue, locale) / 100, 10) : keyedValue

      if (value === "") value = null

      if (value && negative) value = -value
      if (isNaN(value)) {
        if (!isValidNumberInput) return
        value = null
      }
    } else if (parsedType === "currency") {
      const isValidNumberInput = numberInputRegex.test(keyedValue)
      value = unformatCurrency(keyedValue, locale)

      if (isNaN(value)) {
        if (!isValidNumberInput) {
          const prevValue = formatCurrency(stateKeyedValue, locale)
          if (!isNaN(prevValue) && prevValue !== null) {
            value = prevValue
            keyedValue = prevValue
          } else {
            return
          }
        }

        if (keyedValue && isNaN(keyedValue)) errorFormat = hasToBeNumberMessage
      }

      if (negative) value = -value
    } else if (parsedType === "email") {
      const emailRegex = this.getEmailRegExp()
      value = keyedValue
      if (value && !emailRegex.test(value)) errorFormat = loc`Email format should be "john@doe.com"`
    } else if (parsedType === "emails") {
      const emailRegex = this.getEmailRegExp()
      const validEmails = event.reduce((acc, val) => {
        if (val.value && emailRegex.test(val.value)) acc.push(val.value)
        return acc
      }, [])
      if (validEmails.length !== event.length) errorFormat = loc`Email format should be "john@doe.com"`
      value = validEmails
    } else if (parsedType === "username") {
      value = keyedValue
      if (value) {
        const tenant = getTenant()
        if (!isValidUsername({ username: value, tenant })) {
          errorFormat = loc`Should be a valid email or a valid string ending with @${tenant}`
        }
      }
    } else if (parsedType === "phone") {
      value = keyedValue
      if (value) value = value.replace(/ /g, "")

      const { countryRegExp, callingCode, localNumber } = event || {}
      const phoneRegex = this.getPhoneRegExp({ countryRegExp, callingCode })
      const valueToValidate = countryRegExp || callingCode ? localNumber : value

      if (valueToValidate && !phoneRegex.test(valueToValidate)) errorFormat = loc`Invalid format`
    } else if (parsedType === "checkbox") value = event.target.checked
    else if (parsedType === "radio") value = keyedValue
    else if (parsedType === "switch") value = event?.state?.value ?? false
    else if (["file", "files"].includes(parsedType)) {
      value = []
      keyedValue = Array.from(event.target?.files || [])

      /**
       * With type "file", the value is an array of FormData.
       * Use type "file" + multiple: true if you want to handle each file individually.
       * For instance if you send them to some APIs that support receiving one file at a time.
       *
       * With type "files", the value is a FormData composed of several files.
       * Use type "files" when you want to upload one or several files to a script using the API /api/script/upload-runs.
       * Note that in this case the FormData is still iterable like an array.
       */
      let formData = parsedType === "files" ? new FormData() : null
      for (let i = 0; i < keyedValue.length; i++) {
        const keyedValueItem = keyedValue[i]
        if (keyedValueItem?.errorFormat) {
          errorFormat = keyedValue[i].errorFormat
          continue
        }

        if (parsedType === "file") formData = new FormData()

        const fileName = keyedValueItem.name
        formData.append("name", fileName)
        formData.append(parsedType, keyedValueItem)
        if (fileType) formData.append("type", fileType)
        value.push(formData)
      }

      if (parsedType === "files") {
        // When all files are removed, we force the null value for easier coding in scripts.
        value = value.length ? formData : null
      }
    } else {
      if (parsedType === "iban") value = unformatIban(keyedValue)
      else value = keyedValue === undefined ? event.value : keyedValue

      if (value) errorFormat = checkRegistrationFormat(parsedType, value, regExp)
    }

    if (upperCase === true && value && typeof value === "string") value = value.toUpperCase()
    if (upperCase === true && keyedValue && typeof keyedValue === "string") keyedValue = keyedValue.toUpperCase()

    if (lowerCase === true && value && typeof value === "string") value = value.toLowerCase()
    if (lowerCase === true && keyedValue && typeof keyedValue === "string") keyedValue = keyedValue.toLowerCase()

    if (fieldName?.includes(".") && !noLodash) {
      const { _isNew } = patch
      patch = toPatch(obj, fieldName, value)
      patch._isNew = _isNew
    } else patch[fieldName] = value

    if (select && parsedType && parsedType !== "address" && value !== null && value !== undefined && onSelect && typeof onSelect === "function") {
      const onSelectPatch = await onSelect(merge(deepClone(obj), patch)) // Deep clone to avoid mutating "obj"
      if (onSelectPatch && isObject(onSelectPatch) && Object.keys(onSelectPatch).length) patch = merge(deepClone(patch), onSelectPatch)
    }

    let statePatch
    if (stateKeyedValue !== keyedValue) statePatch = { keyedValue }

    updateFieldProps(patch, { obj, field: fieldName, mandatory, label: textLabel, errorFormat, parsedType })

    if (statePatch) this.setState(statePatch)

    if (onSetState && !debounceWait) onSetState(patch, event)
    else this.onSetStateDebounced?.(patch, event)

    setBeforeUnloadListener()
  }

  intlParseFloat = (input, locale) => {
    locale = locale || getLocale()
    let str = formatDecimal(1.1, locale)
    if (str.indexOf(",") >= 0) {
      // French format => replace "," by "." if no "." present (if there is a "." assume the French guy is inputing with . as a decimal separator)
      if (input.indexOf(".") === -1) input = input.replace(",", ".")
    } else {
      if (input.indexOf(",") >= 0 && input.indexOf(".") === -1) {
        input = input.replace(",", ".")
      }
      input = input.replace(/,/g, "")
    }
    return Number(input)
  }

  handleBlur = (event, options = {}) => {
    const { keepValueOnBlur, field: fieldName, obj, mandatory, label, errorFormat } = this.getAllProps()

    // keyedValue: null is required so that the input is formatted after the user leaves the input.
    // It would be nice to find another of doing it because currently it causes a flicker between the previous and next value
    // when the user leaves the input immediately after updating it.
    // When the input is a select, there is no formatting to be done so we can skip this block,
    // which also prevents an accessibility tab navigation bug where the parent element is focused after this one
    // when tab navigating out of it.
    if (!options.isSelect) {
      if (!keepValueOnBlur) this.setState({ keyedValue: null })
    }

    if (options.isNaN) {
      const patch = { [fieldName]: "" }
      updateFieldProps(patch, { obj, field: fieldName, mandatory, errorFormat: "" })
      this.handleChange({ target: { value: "" } })
    } else if (options.isInteger) {
      if (errorFormat?.includes(loc`${label} has to be a number`)) updateFieldProps(obj, { obj, field: fieldName, mandatory, errorFormat: "" })
    } else if (options.isPercentage) {
      if (errorFormat?.includes(loc`${label} has to be a number`)) updateFieldProps(obj, { obj, field: fieldName, mandatory, errorFormat: "" })
    }

    const { onBlur } = this.props
    if (typeof onBlur === "function") onBlur(event)
  }

  handleFocus = event => {
    const { onFocus } = this.props
    if (typeof onFocus === "function") onFocus(event)
  }

  handleDropdownOpen = async event => {
    // This is either called from the onMenuOpen prop of selects, or onOpen of date pickers.
    const { onMenuOpen, onOpen } = this.props
    if (typeof onMenuOpen === "function") onMenuOpen(event)
    else if (typeof onOpen === "function") onOpen(event)

    handleParentTableOverflow({ ref: this.ownRef })
  }

  handleDropdownClose = async event => {
    // This is either called from the onMenuOpen prop of selects, or onOpen of date pickers.
    const { onMenuClose, onClose } = this.props
    if (typeof onMenuClose === "function") onMenuClose(event)
    else if (typeof onClose === "function") onClose(event)

    handleParentTableOverflow({ ref: this.ownRef, toggle: false })
  }

  getReadOnlyBadgeOrRawElement = ({ displayMode, handleClear, dispValue, allProps }) => {
    const label = this.getLabel(allProps)

    if (displayMode === "badge") {
      return (
        <span className="read-only-badge min-h-24px border-1px br-theme border-solid border-gray-lighter font-1-2 leading-2-4 mr-5px mt-5px pdr-5px inline-flex flex-align-center flex-wrap">
          <span className="flex flex-wrap">
            {typeof handleClear === "function" && (
              <div
                className="inline-flex-center max-h-24px min-h-24px max-w-24px min-w-24px text-danger c-pointer"
                tabIndex="0"
                onClick={handleClear}
                onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: handleClear })}
              >
                <i className="icn-xmark icn-xxs" />
              </div>
            )}
            <span className="font-weight-bold mr-5px">{label}:</span>
          </span>
          {dispValue}
        </span>
      )
    }

    if (displayMode === "raw") {
      return (
        <>
          {label && <span className="font-weight-bold mr-5px">{label}:</span>}
          <span>{dispValue}</span>
        </>
      )
    }
  }

  handleKeyDown = async event => {
    const { onEnter, onEscape, onPaste, onKeyDown } = this.props

    if (onKeyDown) return onKeyDown(event)

    const ignoreEnterKeyDown = event.target.type === "textarea"

    if (event.key === "Enter" && !ignoreEnterKeyDown) {
      event.preventDefault()
      if (onEnter) onEnter()
    } else if (event.key === "Escape") {
      event.preventDefault()
      if (onEscape) onEscape()
    }

    // 110 = key code for the numpad decimal separator
    // this is a different code from dot or comma from elsewhere on the keyboard
    if (event.keyCode === 110 && !ignoreEnterKeyDown) {
      event.preventDefault()
      const decimalSymbol = getDecimalSymbol(getLocale())
      this.handleChange({ target: { value: event.target.value + decimalSymbol } })
    }

    if (onPaste && (event.metaKey || event.ctrlKey) && event.keyCode === 86) {
      const ta = event.target
      const str = await onPaste(event)
      if (str) {
        if (!ta.value) ta.value = str
        else if (ta.selectionStart > 0 && ta.selectionEnd > 0) {
          ta.value = ta.value.substr(0, ta.selectionStart) + str + ta.value.substr(ta.selectionEnd)
        }
        // did not succeed to triger a change event => call handle change directly (hack)
        // const ev2 = new Event('input', { bubbles: true});
        // ta.dispatchEvent(ev2);
        this.handleChange({ target: { value: ta.value } })
      }
    }
  }

  linkFunction = ({ event, labelLinkTo, labelLinkToTarget }) => {
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
    const { navigationRef } = this.state
    event.preventDefault()

    if (checkIfInIframe() || labelLinkToTarget === "_self") {
      navigationRef.history.push(labelLinkTo)
      return
    }

    if (labelLinkTo.startsWith("data")) {
      const openedWindow = window.open()
      openedWindow.document.write(
        '<iframe src="' +
          labelLinkTo +
          '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>',
      )
      return
    }

    const target = labelLinkToTarget || "_blank"
    if (labelLinkTo.startsWith("http")) {
      window.open(labelLinkTo, target)
      return
    }

    window.open(`${window.location.origin}${labelLinkTo}`, target)
  }

  getLabel(allProps) {
    const {
      label,
      labelLinkTo,
      labelLinkToIcon,
      labelLinkToStyle,
      labelLinkToTarget,
      field,
      mandatory,
      showMandatoryHint = mandatory,
    } = allProps || this.getAllProps()
    if (React.isValidElement(label)) return label

    // support the case where label is forced to ""
    let textLabel = !label && label !== "" ? labelFromName(field) : loc(label)
    if (labelLinkTo) {
      const icon = (
        <i
          className={`${labelLinkToIcon || "icn-external-link icn-xs"} text-${labelLinkToStyle || "link"} c-pointer`}
          onClick={event => this.linkFunction({ event, labelLinkTo, labelLinkToTarget })}
        />
      )
      textLabel = (
        <>
          <ControlLabel>{textLabel}</ControlLabel>
          {showMandatoryHint && (
            <span className="form-input-mandatory-hint text-danger">
              <i className="icn-asterisk icn-xxxs relative bottom-5px" />
            </span>
          )}
          {labelLinkTo ? (
            <a href={labelLinkTo} aria-label={`${loc("Link to")} ${labelLinkTo}`} className="ml-5px">
              {icon}
            </a>
          ) : (
            icon
          )}
        </>
      )
    }
    return textLabel
  }

  getPlaceholder(allProps) {
    const { placeholder } = allProps || this.getAllProps()
    return placeholder ? loc(placeholder) : /*typeof label === "string" ? label :*/ undefined
  }

  getAllProps = argProps => {
    const props = argProps || this.props
    const { field, obj = {} } = props

    const configProps = getFieldProps(obj, field)
    const allProps = { ...props, ...configProps }

    allProps.debounceWait = this.getDebounceWait(allProps.debounce)
    const { onSetState, debounceWait, debounceOptions } = allProps
    if (onSetState && debounceWait) this.onSetStateDebounced ??= debounce(onSetState, debounceWait, debounceOptions).bind(this)

    return allProps
  }

  getDebounceWait = debounce => {
    if (debounce === true) return internalDebounce
    if (typeof debounce === "number") return debounce
  }

  getSideLabel = ({ sideLabel, type, obj, field, value, currency }) => {
    if (sideLabel) return sideLabel

    if (type === "currency" && currency) {
      const { currency: targetCurrency, isMultiCurrency } = getUserConfiguration()?.tenant || {}
      if (isMultiCurrency && targetCurrency && currency !== targetCurrency) {
        const amount = this.getParsedValue({ obj, field, value })
        const currencyValue = getCachedCurrenciesMap()?.[currency]?.value

        if (isGreaterThan(amount, 0) && isGreaterThan(currencyValue, 0)) {
          const currencyAmount = amount * currencyValue
          if (isGreaterThanOrEqual(currencyAmount, 0)) return formatCurrency(currencyAmount, getLocale(), targetCurrency)
        }
      }
    }
  }

  getControlLabelWithDebugWithAudit = ({ clClassName = "", allProps } = {}) => {
    const { modelFieldPath, debug, showHistoryModal, entityName, entityRegistration } = this.state
    const {
      type,
      currency,
      showLabel = true,
      help,
      debugUrlQuery,
      debugScriptList,
      controlLabelClassName = "",
      select,
      obj,
      field,
      mandatory,
      showMandatoryHint = mandatory,
      labelLinkTo,
      sideLabel,
      value,
      withAudit,
    } = allProps || this.getAllProps()
    const label = this.getLabel(allProps)
    const _sideLabel = this.getSideLabel({ type, obj, field, currency, sideLabel, value })
    const auditTitle = withAudit?.title || (parseBoolean(withAudit?.myAuditOnly) ? "My audit" : "Audit")

    const controlLabelWithDebug = (
      <>
        {showLabel && label}
        {showMandatoryHint && !labelLinkTo && (
          <span className="form-input-mandatory-hint text-danger">
            <i className="icn-asterisk icn-xxxs relative bottom-5px" />
          </span>
        )}
        {withAudit && (
          <OverlayTrigger placement="top" overlay={<Tooltip id="audit">{loc(auditTitle)}</Tooltip>}>
            <i className="icn-history icn-xs c-pointer text-info ml-5px" onClick={() => this.setState({ showHistoryModal: true })} />
          </OverlayTrigger>
        )}
        {withAudit && showHistoryModal && (
          <HistoryModal
            withAudit={withAudit}
            title={auditTitle}
            type={type}
            select={select}
            entityName={entityName}
            entityRegistration={entityRegistration}
            field={modelFieldPath || field}
            onClose={() => this.setState({ showHistoryModal: false })}
          />
        )}
        {_sideLabel && <span className="right-label">{_sideLabel}</span>}
        {debug && (
          <DebugPopover
            help={help}
            debug={debug}
            select={select}
            debugUrlQuery={debugUrlQuery}
            value={select && obj?.[field]}
            modelFieldPath={modelFieldPath}
            debugScriptList={debugScriptList}
          />
        )}
      </>
    )

    return React.isValidElement(label) || !showLabel ? (
      controlLabelWithDebug
    ) : (
      <ControlLabel className={applyClasses({ [controlLabelClassName]: true, [clClassName]: true })} htmlFor={modelFieldPath}>
        {controlLabelWithDebug}
      </ControlLabel>
    )
  }

  readOnlyControlInArrayOrInForm = ({ label, control, linkTo, addClassName, helperDisplay, allProps }) => {
    const { modelFieldPath } = this.state
    const {
      obj,
      colProps,
      inArray,
      openLinkInNewTab,
      rightAddOn,
      inline, // legacy, use displayMode: "inline" instead
      formGroupClassName = "",
      formGroupStyle = {},
      action,
      actionClassName = "",
      actionIcon,
      actionTooltip = "",
      readOnlyAction,
      readOnly,
      displayMode,
      handleClear,
    } = allProps
    let { fcStaticClassName = "" } = allProps

    const ControlLabelWithDebugWithAudit = this.getControlLabelWithDebugWithAudit

    if (addClassName) {
      if (fcStaticClassName) fcStaticClassName += " "
      fcStaticClassName += addClassName
    }

    if (linkTo) {
      control = (
        <Link
          // Skip React internal properties & circular references
          to={replaceUrlParams(linkTo, obj, { skipNodesWithKey: ["$$typeof"], skipCircularReferences: true })}
          target={linkTo.startsWith("http") || openLinkInNewTab ? "_blank" : undefined}
        >
          {control}
        </Link>
      )
    }

    if (inArray) {
      if (fcStaticClassName) control = <span className={fcStaticClassName}>{control}</span>
      return (
        <>
          {rightAddOn && typeof rightAddOn === "string" ? control + ` ${rightAddOn}` : control || ""}
          {helperDisplay}
        </>
      )
    }

    if (inline || displayMode === "inline") {
      return (
        <Row>
          <Col xs={4} sm={3} md={2} lg={2}>
            <ControlLabelWithDebugWithAudit clClassName="leading-3-75" />
          </Col>
          <Col xs={8} sm={9} md={10} lg={10}>
            <FormControl.Static id={modelFieldPath} aria-label={label} className={fcStaticClassName}>
              {control}
            </FormControl.Static>
            {helperDisplay}
          </Col>
        </Row>
      )
    }

    if (readOnly) {
      const readOnlyBadgeOrRawElement = this.getReadOnlyBadgeOrRawElement({ displayMode, dispValue: control, handleClear, allProps })
      if (readOnlyBadgeOrRawElement) return readOnlyBadgeOrRawElement
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        <FormControl.Static
          id={modelFieldPath}
          aria-label={label}
          className={`${fcStaticClassName}${readOnlyAction && action ? " form-control-static-action" : ""}`}
        >
          {control}
          {readOnlyAction && action && (
            <span className={"c-pointer " + actionClassName} onClick={action} onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}>
              <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-search icn-sm"} tooltip={actionTooltip} />
            </span>
          )}
        </FormControl.Static>
        {helperDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  datetimeInput = ({ parsedValue, errorDisplay, helperDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      colProps,
      autoComplete = defaultAutoComplete,
      inArray,
      formGroupClassName = "",
      formGroupStyle = {},
      readOnly,
      disabled,
      showDatetimeInput = true,
      fcClassName = "",
      dateFormat,
      timeFormat,
      isValidDate,
      splitTimeControl = false,
      dropdownMinutesInterval = 30,
      obj,
      timeZone: propTimeZone,
      isBirthDate,
      displayMode,
      handleClear,
    } = allProps
    const locale = getLocale()
    const useDateNoTime = getConfigAtPath("useDateNoTime")
    const timeZone = isBirthDate ? "Europe/London" : propTimeZone || obj?.timeZone || getTimeZone()

    const format = dateFormat || momentDateFormat(locale)
    if (readOnly || disabled) {
      const _format = format + " " + (timeFormat || "H:mm")
      // const dispValue = parsedValue ? (commonUtilsIsValidDate(parsedValue) ? formatDateTime(parsedValue, locale) : parsedValue.toString()) : "" // this code is to avoid errors when data is wrong
      const dispValue = parsedValue
        ? commonUtilsIsValidDate(parsedValue)
          ? !useDateNoTime && timeZone
            ? moment(parsedValue).tz(timeZone).format(_format)
            : moment(parsedValue).format(_format)
          : parsedValue.toString()
        : "" // this code is to avoid errors when data is wrong

      if (readOnly) {
        const readOnlyBadgeOrRawElement = this.getReadOnlyBadgeOrRawElement({ displayMode, dispValue, handleClear, allProps })
        if (readOnlyBadgeOrRawElement) return readOnlyBadgeOrRawElement
      }

      if (inArray) return dispValue

      const formInput = (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle}>
          <ControlLabelWithDebugWithAudit />
          <InputGroup data-disabled>
            <InputGroup.Addon className="form-input-addon">
              <i className="icn-calendar icn-sm" />
            </InputGroup.Addon>
            <div className="form-control" id={modelFieldPath} disabled>
              {dispValue}
            </div>
          </InputGroup>
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    const viewDate = isBirthDate && !(keyedValue || parsedValue) ? new Date(Date.UTC(2000, 0, 1)) : keyedValue || parsedValue
    const inputValue = keyedValue ?? parsedValue ?? ""
    const control = (
      <FormInputDatetime
        open={getOptions("showDatePicker")} // https://github.com/arqex/react-datetime?tab=readme-ov-file#api
        closeOnSelect
        ControlLabelWithDebugWithAudit={ControlLabelWithDebugWithAudit}
        input={showDatetimeInput}
        className={fcClassName}
        dateFormat={format}
        timeFormat={timeFormat || "H:mm"}
        splitTimeControl={splitTimeControl}
        dropdownMinutesInterval={dropdownMinutesInterval}
        autoComplete={autoComplete}
        viewDate={viewDate}
        onOpen={this.handleDropdownOpen}
        onClose={this.handleDropdownClose}
        inputProps={{
          placeholder: loc(placeholder || format),
          disabled,
          onChange: event => {
            const dateFromValue = event.target.value
            const formattedDate = moment(dateFromValue, format, true)
            if (!formattedDate.isValid()) this.handleChange(dateFromValue)
          },
          onKeyDown: event => {
            if (["Enter", "Tab"].includes(event.key) && event?.target?.value && format && errorDisplay) {
              const formattedDate = moment(event.target.value, format)
              if (formattedDate.isValid()) this.handleChange(formattedDate)
            }
          },
          onKeyUp: event => {
            const dateFromValue = event?.target?.value
            if (event.key.match(/[0-9]/g) && dateFromValue && format) {
              const monthIndex = format?.indexOf("MM") ?? -1
              if (monthIndex !== -1) {
                const monthFormat = format.substring(monthIndex + 2, monthIndex + 3)
                const regex = new RegExp(`${monthFormat}`, "g")
                if (format.charAt(dateFromValue.length) === monthFormat && (dateFromValue.match(regex)?.length ?? 0) < 2)
                  this.handleChange(dateFromValue + monthFormat)
              }
            } else if (dateFromValue && format && dateFromValue.length > 1 && event.key === dateFromValue.charAt(dateFromValue.length - 2)) {
              this.handleChange(dateFromValue.substring(0, dateFromValue.length - 1))
            }
          },
          value: commonUtilsIsValidDate(inputValue)
            ? !useDateNoTime && timeZone
              ? moment(inputValue).tz(timeZone).format(format)
              : moment(inputValue).format(format)
            : inputValue,
        }}
        locale={locale?.split("-")[1]}
        value={inputValue}
        onChange={dateFromValue => {
          if (typeof dateFromValue === "object" && dateFromValue?._isAMomentObject && dateFromValue.isValid()) this.handleChange(dateFromValue)
        }}
        // onClick={event => setVirtualKeyboardChangeHandler(event , this.handleChange)} // doesn't work
        isValidDate={isValidDate}
        renderView={(_, renderDefault) => (
          <>
            {renderDefault()}
            <CustomButton
              bsStyle="primary"
              fill
              bsSize="small"
              pullRight
              onClick={() => this.handleChange(moment(new Date()))}
            >{loc`Today`}</CustomButton>
          </>
        )}
        timeZone={timeZone}
      />
    )

    if (inArray) {
      return (
        <>
          {control}
          {helperDisplay}
          {errorDisplay}
          <span className="hidden" ref={this.ownRef}></span>
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        {!splitTimeControl && <ControlLabelWithDebugWithAudit />}
        {control}
        {helperDisplay}
        {errorDisplay}
        <span className="hidden" ref={this.ownRef}></span>
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  handleDateChange = (date, timeZone) => {
    if (!getConfigAtPath("useDateNoTime") && timeZone) {
      const now = moment(new Date()).tz(timeZone)
      this.handleChange(moment(date).hour(now.get("hour")).minute(now.get("minute")).second(now.get("second")).millisecond(now.get("millisecond")))
    } else this.handleChange(date)
  }

  dateInput = ({ parsedValue, errorDisplay, helperDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      fcClassName = "",
      readOnly,
      displayMode,
      showDateInput = true,
      isValidDate,
      colProps,
      autoComplete = defaultAutoComplete,
      inArray,
      formGroupClassName = "",
      formGroupStyle = {},
      isBirthDate,
      dateFormat,
      timeFormat,
      handleClear,
      obj,
      timeZone: propTimeZone,
    } = allProps
    const locale = getLocale()
    const disabled = allProps.disabled ? true : false
    const useDateNoTime = getConfigAtPath("useDateNoTime")
    const timeZone = isBirthDate ? "Europe/London" : propTimeZone || obj?.timeZone || getTimeZone()

    // "disabled" is same as "readOnly" in the case of a date in order to keep the same format
    // because <Datetime/> does not have the same format as formatDate() even if they have the same locale
    let format = dateFormat || momentDateFormat(locale)
    const dateFormatLength = format.length
    if (timeFormat) format = format + " - HH:mm"
    if (readOnly || disabled) {
      const dispValue = parsedValue
        ? !useDateNoTime && timeZone
          ? moment(parsedValue).tz(timeZone).format(format)
          : moment(parsedValue).format(format)
        : ""
      if (inArray) {
        return typeof dispValue === "object"
          ? dispValue.toString() // Do not render object to avoid breaking the page rendering
          : dispValue
      }

      if (readOnly) {
        const readOnlyBadgeOrRawElement = this.getReadOnlyBadgeOrRawElement({ displayMode, dispValue, handleClear, allProps })
        if (readOnlyBadgeOrRawElement) return readOnlyBadgeOrRawElement
      }

      const formInput = (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
          <ControlLabelWithDebugWithAudit />
          <InputGroup data-disabled>
            <InputGroup.Addon className="form-input-addon">
              <i className="icn-calendar icn-sm" />
            </InputGroup.Addon>
            <div className="form-control" id={modelFieldPath} disabled>
              {dispValue}
            </div>
          </InputGroup>
          {helperDisplay}
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    const viewDate = isBirthDate && !(keyedValue || parsedValue) ? new Date(Date.UTC(2000, 0, 1)) : keyedValue || parsedValue
    const inputValue = keyedValue || parsedValue || ""

    const inputProps = {
      placeholder: loc(placeholder || format),
      disabled,
      onChange: event => {
        const dateFromValue = event.target.value
        const formattedDate = moment(dateFromValue, format, true)
        if (!formattedDate.isValid()) this.handleChange(dateFromValue)
        else if (timeFormat) this.handleDateChange(formattedDate, timeZone)
      },
      onKeyDown: event => {
        if (["Enter", "Tab"].includes(event.key) && event?.target?.value && format && errorDisplay) {
          const formattedDate = moment(event.target.value, format)
          if (formattedDate.isValid()) this.handleDateChange(formattedDate, timeZone)
        }
      },
      onKeyUp: event => {
        const dateFromValue = event?.target?.value
        if (event.key.match(/[0-9]/g) && dateFromValue && format) {
          const monthIndex = format?.indexOf("MM") ?? -1
          if (monthIndex !== -1) {
            const monthFormat = format.substring(monthIndex + 2, monthIndex + 3)
            // Prefix each character with the \ escape character to force the regex into interpreting each character literally.
            // Else, some date formats like DD.MM.YYYY would not work as expected since . is a special character in regex.
            const regex = new RegExp(`\\${monthFormat.split("").join("\\")}`, "g")
            if (format.charAt(dateFromValue.length) === monthFormat && (dateFromValue.match(regex)?.length ?? 0) < 2) {
              this.handleChange(dateFromValue + monthFormat)
            } else if (
              timeFormat &&
              dateFromValue.length === dateFormatLength &&
              format.charAt(dateFromValue.length) === " " &&
              (dateFromValue.match(regex)?.length ?? 0) === 2 &&
              !dateFromValue.match(new RegExp(`${" "}`, "g"))
            ) {
              this.handleChange(dateFromValue + " - ")
            } else if (
              timeFormat &&
              dateFromValue.length === dateFormatLength + 5 &&
              format.charAt(dateFromValue.length) === ":" &&
              dateFromValue.match(new RegExp(`${" - "}`, "g"))
            ) {
              this.handleChange(dateFromValue + ":")
            }
          }
        } else if (dateFromValue && format && dateFromValue.length > 1 && event.key === dateFromValue.charAt(dateFromValue.length - 2)) {
          this.handleChange(dateFromValue.substring(0, dateFromValue.length - 1))
        }
      },
      value: commonUtilsIsValidDate(inputValue)
        ? !useDateNoTime && timeZone
          ? moment(inputValue).tz(timeZone).format(format)
          : moment(inputValue).format(format)
        : inputValue,
    }

    if (!inputValue) inputProps.value = ""

    const control = (
      <Datetime
        open={getOptions("showDatePicker")} // https://github.com/arqex/react-datetime?tab=readme-ov-file#api
        closeOnSelect
        input={showDateInput}
        className={fcClassName}
        dateFormat={dateFormat || momentDateFormat(locale)}
        timeFormat={timeFormat ? "H:mm" : false}
        viewDate={viewDate}
        autoComplete={autoComplete}
        inputProps={inputProps}
        locale={locale?.split("-")[1]}
        value={inputValue}
        onOpen={this.handleDropdownOpen}
        onClose={this.handleDropdownClose}
        onChange={dateFromValue => {
          if (
            typeof dateFromValue === "object" &&
            dateFromValue?._isAMomentObject &&
            dateFromValue.isValid() &&
            // when displayTimeZone is defined dateFromValue is always a valid moment even with missing date parts
            // so here we check the length of the formatted date against the length of the format
            // otherwise typing is difficult
            (useDateNoTime || !dateFromValue._f || !dateFromValue._i || dateFromValue._f.length === dateFromValue._i.length)
          ) {
            this.handleDateChange(dateFromValue, timeZone)
          }
        }}
        // onClick={event => setVirtualKeyboardChangeHandler(event , this.handleDateChange)} // doesn't work
        isValidDate={isValidDate}
        renderView={(_, renderDefault) => (
          <>
            {renderDefault()}
            <CustomButton
              bsStyle="primary"
              fill
              bsSize="small"
              pullRight
              onClick={() => this.handleDateChange(!useDateNoTime && timeZone ? moment(new Date()).tz(timeZone) : moment(new Date()), timeZone)}
            >{loc`Today`}</CustomButton>
          </>
        )}
        displayTimeZone={!useDateNoTime ? timeZone : undefined}
      />
    )

    if (inArray) {
      return (
        <>
          {control}
          {helperDisplay}
          {errorDisplay}
          <span className="hidden" ref={this.ownRef}></span>
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {control}
        {helperDisplay}
        {errorDisplay}
        <span className="hidden" ref={this.ownRef}></span>
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  selectInput = ({ parsedValue, errorDisplay, helperDisplay, placeholder: argsPlaceholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, selectOptions: selectOptionsState, defaultAutoComplete, keyedValue, debug } = this.state
    const {
      obj,
      select,
      filterOption,
      action,
      actionIcon,
      actionTooltip = "",
      actionClassName = "",
      debounceWait,
      debounceOptions,
      creatable,
      readOnly,
      colProps,
      showCopy,
      hideChevron,
      onKeyDown,
      onInputChange,
      autoComplete = defaultAutoComplete,
      inArray,
      formGroupClassName = "",
      formGroupStyle = {},
      buttonGroupClassName = "",
      defaultOptions = true,
      hideClear,
      help,
      richValuesList,
      autoFocus,
      button,
      splitButton,
      filteredValues,
      onSelect,
      openMenuOnClick,
      onClick,
      noOptionsMessage = () => loc("No data"),
      loadingMessage = () => loc("Loading"),
      fcClassName = "",
      linkTo,
      rightAddOn,
      badge,
      bsStyle,
      bsSize,
      label, // for the select input the label comes from the props, not the args, which is weird
      pullRight,
      showUnknownLabelHint = true,
      keepValueOnBlur,
      isLoading,
      selectValueLabel,
      isMulti,
      menuPlacement, // "bottom" (default), "top", "auto" (will flip when there isn't enough space below the control)
      displayMode,
      displayModeOptions,
      fixedSelectOptions,
      selectableSelectOptions,
      disabledSelectOptions,
      enabledSelectOptions,
    } = allProps
    const disabled = allProps.disabled ? true : false
    let selectOptions = selectOptionsState
    let { selectListIsLoaded } = this.state

    const placeholder = argsPlaceholder || loc("Select") + "..."

    const keepInputProps = keepValueOnBlur
      ? {
          onInputChange: (value, { action }) => {
            const warningFormat = value ? loc`Caution, the value is incomplete and will not be considered.` : undefined
            if (action !== "input-blur" && action !== "menu-close")
              this.setState({
                keyedValue: value,
                warningFormat,
              })
          },
          inputValue: keyedValue,
        }
      : {}

    if (typeof select === "function") {
      // autocompletion
      if (readOnly) {
        return this.readOnlyControlInArrayOrInForm({ control: parsedValue || "", linkTo, helperDisplay, allProps })
      }

      let loadOptions
      if (!debounceWait) loadOptions = select
      else {
        // Warning:
        // The lib react-async bugs if the loadOptions function does not return an array.
        // One common bug is when no match is found; after that, new matches are not displayed anymore.
        // So we must ensure that we always return one, even empty.
        const debouncedLoadOptions = (query, callback) => {
          // Ignore when having 1 character // this will fix the cursor problem when deleting the last character
          if (query?.length === 1) return []
          select(query).then((resp = []) => callback(resp))
        }
        loadOptions = debounce(debouncedLoadOptions, debounceWait, debounceOptions)
      }

      const formControl = creatable ? (
        <AsyncCreatableSelect
          {...keepInputProps}
          id={modelFieldPath}
          {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
          loadOptions={loadOptions}
          isDisabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={this.handleChange}
          onBlur={event => this.handleBlur(event, { isSelect: true })}
          onFocus={this.handleFocus}
          onMenuClose={this.handleDropdownClose}
          onMenuOpen={this.handleDropdownOpen}
          // onClick={() => setVirtualKeyboardChangeHandler(this.handleChange, "")} // doesn't work
          defaultOptions={defaultOptions}
          className="select-group"
          classNamePrefix="select"
          openMenuOnClick={openMenuOnClick}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          formatCreateLabel={text => `${loc`Create`} "${text}"`}
          value={parsedValue ? { parsedValue, label: selectValueLabel || parsedValue } : undefined}
          components={getSelectComponents({ isClearable: !hideClear, showCopy, hideChevron, isLoading })}
          aria-labelledby={modelFieldPath}
          menuPlacement={menuPlacement}
          isMulti={isMulti}
        />
      ) : (
        <AsyncSelect
          {...keepInputProps}
          id={modelFieldPath}
          autoFocus={autoFocus}
          {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
          loadOptions={loadOptions}
          isDisabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onBlur={event => this.handleBlur(event, { isSelect: true })}
          onFocus={this.handleFocus}
          onMenuClose={this.handleDropdownClose}
          onMenuOpen={this.handleDropdownOpen}
          onKeyDown={onKeyDown}
          onChange={this.handleChange}
          // onClick={() => setVirtualKeyboardChangeHandler(this.handleChange, "")} // doesn't work
          onInputChange={onInputChange}
          defaultOptions={defaultOptions}
          className="select-group"
          classNamePrefix="select"
          openMenuOnClick={openMenuOnClick}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          value={parsedValue && typeof parsedValue === "string" ? { parsedValue, label: selectValueLabel || parsedValue } : undefined}
          components={getSelectComponents({ isClearable: !hideClear, showCopy, hideChevron, isLoading })}
          aria-labelledby={modelFieldPath}
          menuPlacement={menuPlacement}
          isMulti={isMulti}
        />
      )

      if (action) {
        if (inArray) {
          return (
            <InputGroup
              className="form-input-select-action-in-array" // DOM hint
            >
              <InputGroup.Addon
                className={"c-pointer " + actionClassName}
                onClick={action}
                onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
              >
                <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
              </InputGroup.Addon>
              {formControl}
              <DebugPopover debug={debug} help={help} modelFieldPath={modelFieldPath} />
              <span className="hidden" ref={this.ownRef}></span>
            </InputGroup>
          )
        }

        const formInput = (
          <FormGroup
            bsClass={`form-group ${formGroupClassName}`}
            className="form-input-select-action" // DOM hint
            style={formGroupStyle}
            {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
          >
            <ControlLabelWithDebugWithAudit />
            <InputGroup>
              {formControl}
              <InputGroup.Addon
                className={"c-pointer " + actionClassName}
                onClick={action}
                onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
              >
                <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
              </InputGroup.Addon>
            </InputGroup>
            {helperDisplay}
            {errorDisplay}
            <span className="hidden" ref={this.ownRef}></span>
          </FormGroup>
        )

        return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
      }

      if (inArray) {
        return (
          <>
            {formControl}
            {helperDisplay}
            {errorDisplay}
            <span className="hidden" ref={this.ownRef}></span>
          </>
        )
      }

      const formInput = (
        <FormGroup
          bsClass={`form-group ${formGroupClassName}`}
          className="form-input-select-function" // DOM hint
          style={formGroupStyle}
          {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
        >
          <ControlLabelWithDebugWithAudit />
          {formControl}
          {helperDisplay}
          {errorDisplay}
          <span className="hidden" ref={this.ownRef}></span>
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    if (Array.isArray(select)) {
      const locale = getLocale()

      selectOptions = select.map(it => {
        if (typeof it === "string" || typeof it === "number" || it instanceof Date) {
          const label = it instanceof Date ? formatDate(it, locale) : it
          return { value: it, label: rightAddOn ? `${label} ${rightAddOn}` : label }
        }
        if (it?.label) {
          if (rightAddOn) it.label += ` ${rightAddOn}`
          if (typeof it.label === "string") it.label = it.isTranslated === false ? it.label : loc(it.label)
          return it
        }
        return { ...it, label: rightAddOn ? `${it?.value} ${rightAddOn}` : it?.value }
      })
    } else if (typeof select === "string") {
      // A special way of doing things here : the functions getRichValues and getValues
      // return nothing when the list is being fetched but allow for a callback when they are.
      // Here the render of this component is triggered from within the render (which in general must be avoided)
      // by the callback, and at the second render the functions would return the values instead.
      // Wrap: getRichValues takes an options object (handleLoaded + richValuesList), getValues the plain (name, handleLoaded).
      const _getValues = richValuesList ? (name, handleLoaded) => getRichValues(name, { handleLoaded, richValuesList }) : getValues
      selectOptions = _getValues(replaceUrlParams(select, obj), () => this.setState({ selectListIsLoaded: true })) // get list from server (reference table)
    }

    // Dynamic lists are handled earlier so if upon reaching this code
    // there is no selectOptions then the animation loaded will stay displayed.
    // However this is more a configuration bug than a coding one, so we don't support this case.
    if (typeof select !== "string" || selectOptions?.length > 0) selectListIsLoaded = true
    selectOptions = selectOptions || []

    if (Array.isArray(filteredValues)) {
      selectOptions = selectOptions.filter(selectionOption => filteredValues.includes(selectionOption.value))
    }

    let selectOption
    if (parsedValue instanceof Date) selectOption = selectOptions.find(item => item.value.getTime?.() === parsedValue.getTime())
    else selectOption = selectOptions.find(item => item.value === parsedValue)

    if (!selectOption && parsedValue && typeof parsedValue !== "object") {
      selectOptions = [
        ...selectOptions,
        {
          value: parsedValue,
          label: creatable ? parsedValue : showUnknownLabelHint ? `${parsedValue} (???)` : parsedValue,
        },
      ]
    }

    if (readOnly) {
      // Several values, ex: a page config field with an array path ("tags[].tag")
      if (Array.isArray(parsedValue)) {
        const labels = parsedValue.map(
          code => selectOptions.find(option => option.value === code)?.label ?? (showUnknownLabelHint ? `${code} (???)` : code),
        )
        return this.readOnlyControlInArrayOrInForm({
          control: !labels.length || selectListIsLoaded ? labels.join(", ") : <i className="icn-circle-notch icn-sm icn-spin font-theme text-gray" />,
          linkTo,
          helperDisplay,
          allProps,
        })
      }

      if (badge && selectOption) {
        const badge = (
          <CustomButton
            className={fcClassName + (onClick ? "" : " c-default pointer-events-none") + " btn-badge"}
            bsStyle={selectOption.style || "info"}
            bsSize="xs"
            fill
            round
            onClick={onClick}
            tabIndex={onClick ? "0" : "-1"}
          >
            {selectOption.label}
          </CustomButton>
        )

        const formInput = label ? (
          <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
            <ControlLabelWithDebugWithAudit />
            <br />
            {badge}
          </FormGroup>
        ) : (
          badge
        )

        return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
      }

      return this.readOnlyControlInArrayOrInForm({
        control:
          (![undefined, null, ""].includes(parsedValue) &&
            (selectOption ? (
              selectOption.label
            ) : selectListIsLoaded ? (
              showUnknownLabelHint ? (
                `${parsedValue} (???)`
              ) : (
                parsedValue
              )
            ) : (
              <i className="icn-circle-notch icn-sm icn-spin font-theme text-gray" />
            ))) ||
          "",
        linkTo,
        helperDisplay,
        allProps,
      })
    }

    if (creatable) {
      let itemValue = selectOptions.filter(option => option.value === parsedValue)
      let selectControl = (
        <CreatableSelect
          id={modelFieldPath}
          className={"select-group " + (fcClassName || "")}
          classNamePrefix="select"
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={itemValue}
          options={selectOptions}
          onChange={this.handleChange}
          {...keepInputProps}
          onBlur={event => this.handleBlur(event, { isSelect: true })}
          onFocus={this.handleFocus}
          onMenuClose={this.handleDropdownClose}
          onMenuOpen={this.handleDropdownOpen}
          // onClick={() => setVirtualKeyboardChangeHandler(this.handleChange, "")} // doesn't work
          formatCreateLabel={text => `${loc`Create`} "${text}"`}
          isDisabled={disabled || readOnly}
          openMenuOnClick={openMenuOnClick}
          noOptionsMessage={noOptionsMessage}
          loadingMessage={loadingMessage}
          components={getSelectComponents({ isClearable: !hideClear, showCopy, hideChevron, isLoading })}
          filterOption={filterOption || filterOptionFunction}
          aria-labelledby={modelFieldPath}
          menuPlacement={menuPlacement}
        />
      )

      // watch carefully here: the selectControl variable is overriden
      // but its previous value is used just before
      if (action) {
        selectControl = (
          <InputGroup>
            <InputGroup.Addon
              className={"c-pointer " + actionClassName}
              onClick={action}
              onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
            >
              <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
            </InputGroup.Addon>
            {selectControl}
          </InputGroup>
        )
      }

      if (inArray) {
        return (
          <>
            {selectControl}
            {helperDisplay}
            {errorDisplay}
            <span className="hidden" ref={this.ownRef}></span>
          </>
        )
      }

      const formInput = (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
          <ControlLabelWithDebugWithAudit />
          {selectControl}
          {helperDisplay}
          {errorDisplay}
          <span className="hidden" ref={this.ownRef}></span>
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    let selectedOption
    if (parsedValue instanceof Date) selectedOption = selectOptions.find(option => option.value.getTime?.() === parsedValue.getTime())
    else selectedOption = selectOptions.find(option => option.value === parsedValue)

    const isLoadingList = isLoading || ((parsedValue || parsedValue === 0 ? true : false) && !selectListIsLoaded)
    const dropdownLabel = splitButton ? selectedOption?.label || (showUnknownLabelHint ? `${parsedValue} ???` : parsedValue) : ""

    applySelectOptions({ options: selectOptions, disabledSelectOptions, enabledSelectOptions, fixedSelectOptions, selectableSelectOptions })

    // This is the default select, which is overriden depending on the chosen display options.
    let selectControl = (
      <Select
        id={modelFieldPath}
        aria-labelledby={modelFieldPath}
        className={"select-group " + (fcClassName || "")}
        classNamePrefix="select"
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={
          isLoadingList
            ? ""
            : selectOptions.filter(o => (parsedValue instanceof Date ? o.value.getTime?.() === parsedValue.getTime() : o.value === parsedValue))
        }
        options={selectOptions}
        isDisabled={disabled || readOnly}
        onChange={this.handleChange}
        onSelect={onSelect}
        onMenuClose={this.handleDropdownClose}
        onMenuOpen={this.handleDropdownOpen}
        // onClick={() => setVirtualKeyboardChangeHandler(this.handleChange, "")} // doesn't work
        openMenuOnClick={openMenuOnClick}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={loadingMessage}
        components={getSelectComponents({ isClearable: !hideClear, showCopy, hideChevron, isLoading: isLoadingList })}
        filterOption={filterOption || filterOptionFunction}
        onBlur={event => this.handleBlur(event, { isSelect: true })}
        onFocus={this.handleFocus}
        tabSelectsValue={false}
        menuPlacement={menuPlacement}
        styles={selectNativeStyles}
      />
    )

    if (button || displayMode === "button") {
      selectControl = (
        // this div is important to get buttons under the label
        <div
          className={
            "form-input-select-button " + // DOM hint
            (pullRight ? "pull-right" : "")
          }
        >
          <ButtonGroup
            bsClass={"btn-group " + buttonGroupClassName}
            className={disabled || readOnly ? "disabled" : ""}
            {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
          >
            {selectOptions.map((item, key) => {
              const isSelected = item.value || item.value === 0 ? item.value === parsedValue : key === 1
              return (
                <CustomButton
                  key={item.value + key}
                  disabled={disabled || readOnly}
                  bsStyle={disabled || readOnly ? "default" : bsStyle || "primary"}
                  bsSize={bsSize === "default" ? undefined : bsSize || "small"}
                  fill={isSelected}
                  onClick={() => this.handleChange({ value: isSelected ? undefined : item.value })}
                  className={(item.className || "") + (isSelected ? " selected" : "")}
                >
                  {item.icon && <i className={item.icon} />}
                  {item.labelHtml ? (
                    <span dangerouslySetInnerHTML={{ __html: sanitize(item.labelHtml) }} />
                  ) : typeof item.label === "string" ? (
                    loc(item.label)
                  ) : (
                    item.label
                  )}
                </CustomButton>
              )
            })}
            {action && !disabled && !readOnly && (
              <CustomButton
                key="search"
                simple
                className="inline-flex-center"
                bsStyle={disabled || readOnly ? "default" : "primary"}
                onClick={action}
                onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
              >
                <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-search icn-sm"} tooltip={actionTooltip} />
              </CustomButton>
            )}
          </ButtonGroup>
        </div>
      )
    }

    // An alternative to this option is to use the card TileGrid
    if (displayMode === "grid") {
      const { colsRepeat } = displayModeOptions || {}
      const gridClassName = `grid-cols-${colsRepeat?.sm || 4} xs_grid-cols-${colsRepeat?.xs || 1}`

      selectControl = (
        // this div is important to get buttons under the label
        <div className={`grid grid-gap-5px ${gridClassName}`}>
          {selectOptions.map((item, key) => {
            const isSelected = item.value || item.value === 0 ? item.value === parsedValue : key === 1
            return (
              <div
                key={item.value + key}
                className="tile-btn"
                data-is-selected={isSelected}
                onClick={() => this.handleChange({ value: isSelected ? undefined : item.value })}
              >
                <div className="tile-inner">
                  <div className="tile-selected-hint">
                    <i className="icn-check icn-xs text-white" />
                  </div>

                  <div className="tile-icon">
                    <i className={item.icon} />
                  </div>

                  <div className="tile-text">
                    {item.labelHtml ? (
                      <span dangerouslySetInnerHTML={{ __html: sanitize(item.labelHtml) }} />
                    ) : typeof item.label === "string" ? (
                      loc(item.label)
                    ) : (
                      item.label
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    if (splitButton || displayMode === "splitButton") {
      selectControl = (
        <Dropdown
          id={modelFieldPath}
          className="form-input-select-split-button" // DOM hint
          bsSize="xs"
          onClick={event => {
            const { target } = event
            target.focus()
            target.nextSibling?.click()
          }}
          onToggle={value => (value ? this.handleDropdownOpen() : this.handleDropdownClose())}
        >
          <CustomButton bsStyle={selectedOption?.style || "primary"} fill tabIndex="-1">
            {dropdownLabel}
          </CustomButton>
          <Dropdown.Toggle
            bsStyle={`${selectedOption?.style || "primary"} btn-fill`}
            onKeyDown={onDropdownToggleKeyDown}
            aria-label={dropdownLabel}
          />
          <Dropdown.Menu className="br-theme overflow-hidden border-0">
            {selectOptions
              .filter(option => option.value !== parsedValue)
              .map((option, key) => (
                <CustomButton
                  key={key}
                  bsStyle={selectedOption?.style || "primary"}
                  bsSize="small"
                  fill
                  className="btn-simple white-space-normal w-100 text-left br-0 m-0 inline-flex align-items-center min-w-200px"
                  onClick={event => {
                    event.stopPropagation()
                    this.handleChange({ value: option.value })
                  }}
                  onKeyDown={onDropdownMenuButtonKeyDown}
                >
                  {option.label}
                </CustomButton>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      )
    }

    if (inArray) {
      return (
        <>
          <DebugPopover debug={debug} help={help} modelFieldPath={modelFieldPath} select={select} />
          {colProps ? <Col {...colProps}>{selectControl}</Col> : selectControl}
          {helperDisplay}
          {errorDisplay}
          <span className="hidden" ref={this.ownRef}></span>
        </>
      )
    }

    if (action) {
      let formInput
      if (button) {
        // action is already handled
        formInput = (
          <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
            <ControlLabelWithDebugWithAudit />
            {selectControl}
            {helperDisplay}
            {errorDisplay}
            <span className="hidden" ref={this.ownRef}></span>
          </FormGroup>
        )
      } else {
        formInput = (
          <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
            <ControlLabelWithDebugWithAudit />
            <InputGroup>
              {selectControl}
              <InputGroup.Addon
                className={"c-pointer " + actionClassName}
                onClick={action}
                onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
              >
                <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
              </InputGroup.Addon>
            </InputGroup>
            {helperDisplay}
            {errorDisplay}
            <span className="hidden" ref={this.ownRef}></span>
          </FormGroup>
        )
      }

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {selectControl}
        {helperDisplay}
        {errorDisplay}
        <span className="hidden" ref={this.ownRef}></span>
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  checkboxInput = ({ parsedValue, label, errorDisplay, helperDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, defaultAutoComplete } = this.state
    const { numberId } = this.state
    const {
      readOnly,
      colProps,
      inArray,
      divClassName,
      formGroupClassName = "",
      formGroupStyle = {},
      showPlaceholder = true,
      autoComplete = defaultAutoComplete,
    } = allProps
    const disabled = allProps.disabled ? true : false
    const _numberId = `${modelFieldPath || ""}${numberId}`
    const placeholderLabel = showPlaceholder ? (placeholder ?? label) : null
    const placeholderLabelInArray = showPlaceholder ? placeholder : null

    if (readOnly) {
      if (inArray) {
        return (
          <>
            <CustomCheckbox
              numberId={_numberId}
              label={placeholderLabelInArray}
              checked={parsedValue || false}
              disabled={true}
              autoComplete={autoComplete}
            />
            {helperDisplay}
            {errorDisplay}
          </>
        )
      }

      const formInput = (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
          <ControlLabelWithDebugWithAudit />
          <CustomCheckbox
            numberId={_numberId}
            checked={parsedValue || false}
            disabled={true}
            label={React.isValidElement(placeholderLabel) ? "" : placeholderLabel}
            autoComplete={autoComplete}
          />
          {helperDisplay}
          {errorDisplay}
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    if (inArray) {
      return (
        <>
          <CustomCheckbox
            numberId={_numberId}
            checked={parsedValue || false}
            disabled={disabled}
            onChange={this.handleChange}
            autoComplete={autoComplete}
            label={placeholderLabelInArray}
          />
          {helperDisplay}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        <CustomCheckbox
          numberId={_numberId}
          divClassName={divClassName}
          label={React.isValidElement(placeholderLabel) ? "" : inArray ? "" : placeholderLabel}
          checked={parsedValue || false}
          disabled={disabled}
          onChange={this.handleChange}
          autoComplete={autoComplete}
        />
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  /**
   * Avoid using this type of input because it has often accessibility issues.
   */
  switchInput = ({ parsedValue, label, errorDisplay, helperDisplay, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, defaultAutoComplete } = this.state

    const {
      readOnly,
      colProps,
      inArray,
      formGroupClassName = "",
      formGroupStyle = {},
      onText = "",
      offText = "",
      autoComplete = defaultAutoComplete,
    } = allProps
    const disabled = allProps.disabled ? true : false

    if (readOnly) {
      if (inArray) return <Switch label="" onText={onText} offText={offText} value={parsedValue || false} disabled={true} />

      const formInput = (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
          <ControlLabelWithDebugWithAudit />
          <Suspense fallback={null}>
            <Switch
              onText={onText}
              offText={offText}
              value={parsedValue || false}
              disabled={true}
              label={React.isValidElement(label) ? "" : label}
              wrapperClass="pull-right"
              autoComplete={autoComplete}
            />
          </Suspense>
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    if (inArray) {
      return (
        <Suspense fallback={null}>
          <Switch onText={onText} offText={offText} disabled={disabled} value={parsedValue} wrapperClass="pull-right" autoComplete={autoComplete} />
        </Suspense>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        <span
          className="switch-accessible-wrapper"
          onKeyDown={event => {
            const _value = { value: !parsedValue }
            handleAccessibleOnKeyDown({
              event,
              fn: () =>
                this.handleChange({
                  target: _value,
                  state: _value,
                }),
            })
          }}
        >
          <Suspense fallback={null}>
            <Switch
              label={React.isValidElement(label) ? "" : inArray ? "" : label}
              onText={onText}
              offText={offText}
              value={parsedValue || false}
              disabled={disabled}
              onChange={this.handleChange}
              wrapperClass="pull-right"
              autoComplete={autoComplete}
            />
          </Suspense>
        </span>
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  radioInput = ({ label, parsedValue, errorDisplay, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, defaultAutoComplete, numberId } = this.state
    const {
      option,
      readOnly,
      colProps,
      inArray,
      formGroupClassName = "",
      formGroupStyle = {},
      options,
      inline,
      displayMode,
      autoComplete = defaultAutoComplete,
    } = allProps
    const disabled = allProps.disabled ? true : false

    if (readOnly) {
      if (inArray) {
        return (
          <CustomRadio
            label=""
            value={parsedValue}
            disabled={true}
            name={modelFieldPath}
            option={option}
            options={options}
            inline={inline}
            displayMode={displayMode}
            autoComplete={autoComplete}
          />
        )
      }

      const formInput = (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle}>
          <ControlLabelWithDebugWithAudit />
          <CustomRadio
            label={label}
            value={parsedValue}
            disabled={true}
            name={modelFieldPath}
            option={option}
            options={options}
            inline={inline}
            displayMode={displayMode}
            autoComplete={autoComplete}
          />
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    const _numberId = `${modelFieldPath || ""}${numberId}`

    const control = (
      <CustomRadio
        number={_numberId}
        label={label}
        value={parsedValue}
        disabled={disabled}
        onClick={this.handleChange}
        name={modelFieldPath}
        option={option}
        options={options}
        inline={inline}
        autoComplete={autoComplete}
      />
    )

    if (inArray) {
      return (
        <>
          {/* <DebugPopover debug={debug} help={help} modelFieldPath={modelFieldPath} /> */}
          {control}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {control}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  highlightMatch = (text, query) => {
    if (!query) return text

    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()

    const index = lowerText.indexOf(lowerQuery)
    if (index === -1) return text

    return (
      <>
        {text.slice(0, index)}
        <span className="form-input-mention-match">{text.slice(index, index + query.length)}</span>
        {text.slice(index + query.length)}
      </>
    )
  }

  insertMention = (username, inputValue) => {
    const textarea = this.textareaRef.current
    if (!textarea) return

    const { mentionStart } = this.state
    if (mentionStart == null) return

    const cursorPos = textarea.selectionStart
    const value = inputValue ?? ""

    const mentionToken = `@[${username}]`
    const newValue = value.slice(0, mentionStart) + mentionToken + value.slice(cursorPos)

    this.handleChange({ target: { value: newValue } })

    this.setState({ showMentions: false, mentionStart: null, mentionIndex: 0, mentionQuery: null }, () => {
      textarea.focus()
      const newCursorPos = mentionStart + mentionToken.length
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    })
  }

  textAreaInput = ({ label, parsedValue, errorDisplay, helperDisplay, minMaxDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete, mentionPersons, showMentions, mentionIndex, mentionQuery } = this.state

    const {
      fcClassName = "",
      readOnly,
      colProps,
      inArray,
      formGroupClassName = "",
      formGroupStyle = {},
      rows,
      minRows = 1,
      autoComplete = defaultAutoComplete,
      autoFocus,
      inputRef,
      personsToMention = [],
    } = allProps
    const disabled = allProps.disabled ? true : false

    const inputValue = keyedValue ?? parsedValue ?? ""

    const handleMentionKeyDown = event => {
      if (!showMentions || mentionPersons.length === 0) return

      const isEscape = event.key === "Escape"
      const isArrowDown = event.key === "ArrowDown"
      const isArrowUp = event.key === "ArrowUp"
      const isEnter = event.key === "Enter"

      if (!isEscape && !isArrowDown && !isArrowUp && !isEnter) return

      event.preventDefault()

      if (isEscape) {
        this.setState({ showMentions: false, mentionIndex: 0, mentionQuery: null })
      } else if (isArrowDown) {
        const newIndex = (mentionIndex + 1) % mentionPersons.length
        this.setState({ mentionIndex: newIndex }, () => {
          this.mentionItemRefs[newIndex]?.scrollIntoView({ block: "nearest" })
        })
      } else if (isArrowUp) {
        const newIndex = (mentionIndex - 1 + mentionPersons.length) % mentionPersons.length
        this.setState({ mentionIndex: newIndex }, () => {
          this.mentionItemRefs[newIndex]?.scrollIntoView({ block: "nearest" })
        })
      } else if (isEnter && mentionPersons[mentionIndex]) {
        this.insertMention(mentionPersons[mentionIndex], inputValue)
      }
    }

    const control = (
      <FormControl
        id={modelFieldPath}
        aria-label={label}
        inputRef={ref => {
          this.textareaRef.current = ref

          if (typeof inputRef === "function") {
            inputRef(ref)
          } else if (inputRef) {
            inputRef.current = ref
          }
        }}
        className={fcClassName}
        rows={Math.max(rows || (inputValue ? inputValue.split("\n").length : 0), minRows)}
        componentClass="textarea"
        bsClass="form-control"
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled || readOnly}
        autoComplete={autoComplete}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        autoFocus={autoFocus}
        onKeyDown={event => {
          this.handleKeyDown(event)
          handleMentionKeyDown(event)
        }}
        onInput={({ target }) => {
          if (target.value) {
            // we need to assign twice the height to make the browser auto adjust the height
            target.style.height = "auto"
            target.style.height = target.scrollHeight + 1 + "px"
          } else {
            // let the rows attribute (which honors minRows) and the css min-height define the height
            target.style.height = ""
          }

          if (personsToMention.length) {
            const cursorPos = target.selectionStart
            const value = target.value
            const textBeforeCursor = value.slice(0, cursorPos)
            const atIndex = textBeforeCursor.lastIndexOf("@")
            if (atIndex === -1) {
              this.setState({ showMentions: false, mentionStart: null, mentionIndex: 0, mentionQuery: null })
              return
            }

            const allowedBefore = [" ", "\n", "(", "["]
            const charBeforeAt = textBeforeCursor[atIndex - 1]
            if (atIndex > 0 && !allowedBefore.includes(charBeforeAt)) {
              this.setState({ showMentions: false, mentionStart: null, mentionIndex: 0, mentionQuery: null })
              return
            }

            const query = textBeforeCursor.slice(atIndex + 1)
            if (/\s/.test(query)) {
              this.setState({ showMentions: false, mentionStart: null, mentionIndex: 0, mentionQuery: null })
              return
            }

            const filtered = query === "" ? personsToMention : personsToMention.filter(u => u.toLowerCase().includes(query.toLowerCase()))

            this.setState({
              mentionStart: atIndex,
              mentionPersons: filtered,
              mentionQuery: query,
              showMentions: filtered.length > 0,
              mentionIndex: 0,
            })
          }
        }}
        onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
      />
    )

    const mentionDropdown = showMentions && !readOnly && mentionPersons.length > 0 && (
      <div className="form-input-mention-dropdown">
        {mentionPersons.map((user, i) => (
          <div
            key={user}
            ref={el => (this.mentionItemRefs[i] = el)}
            onMouseDown={event => {
              event.preventDefault()
              this.insertMention(user, inputValue)
            }}
            className={`form-input-mention-item ${i === mentionIndex ? "mention-hover" : ""}`}
          >
            @{this.highlightMatch(user, mentionQuery)}
          </div>
        ))}
      </div>
    )

    const content = (
      <div className="form-input-mention-container">
        {control}
        {mentionDropdown}
      </div>
    )

    if (inArray) return content

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {content}
        {helperDisplay}
        {minMaxDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  numberIntegerInput = ({ label, parsedValue, errorDisplay, helperDisplay, placeholder, parsedType, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      fcClassName = "",
      readOnly,
      rightAddOn,
      linkTo,
      minimumFractionDigits,
      maximumFractionDigits,
      colProps,
      inArray,
      autoComplete = defaultAutoComplete,
      formGroupClassName = "",
      formGroupStyle = {},
      negative,
      action,
      actionClassName = "",
      actionIcon,
      actionTooltip = "",
      hideAddOn = false,
    } = allProps
    const disabled = allProps.disabled ? true : false
    const locale = getLocale()

    if (parsedValue || parsedValue === 0) {
      if (negative) parsedValue = -parsedValue
      if (parsedType === "integer") {
        parsedValue = Math.round(parsedValue)
      } else {
        parsedValue = formatDecimal(parsedValue, locale, { minimumFractionDigits, maximumFractionDigits })
      }
    }

    if (readOnly) {
      return this.readOnlyControlInArrayOrInForm({
        control: parsedValue || parsedValue === 0 ? parsedValue : "",
        linkTo,
        helperDisplay,
        allProps,
      })
    }

    if (parsedValue === 0) parsedValue = "0"
    const inputValue = keyedValue ?? parsedValue ?? ""

    if (inArray) {
      return (
        <>
          <FormControl
            id={modelFieldPath}
            aria-label={label}
            className={fcClassName}
            type="text"
            inputMode="numeric"
            bsClass="form-control"
            value={inputValue}
            disabled={disabled}
            autoComplete={autoComplete}
            onChange={this.handleChange}
            onBlur={event => {
              this.handleBlur(event, { isNaN: isNaN(unformatCurrency(inputValue, locale)), isInteger: parsedType === "integer" })
            }}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
          />
          {helperDisplay}
          {errorDisplay}
        </>
      )
    }

    const formControl = (
      <FormControl
        id={modelFieldPath}
        aria-label={label}
        className={fcClassName}
        type="text"
        inputMode="numeric"
        bsClass="form-control"
        placeholder={placeholder}
        autoComplete={autoComplete}
        value={inputValue}
        disabled={disabled}
        onChange={this.handleChange}
        onBlur={event => {
          this.handleBlur(event, { isNaN: isNaN(unformatCurrency(inputValue, locale)), isInteger: parsedType === "integer" })
        }}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
      />
    )

    const showInputGroup = action || rightAddOn

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {showInputGroup && (
          <InputGroup>
            {formControl}
            {action && !rightAddOn && !hideAddOn && (
              <InputGroup.Addon
                className={"form-input-addon c-pointer " + actionClassName}
                onClick={action}
                onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
              >
                <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
              </InputGroup.Addon>
            )}
            {rightAddOn && <InputGroup.Addon>{rightAddOn}</InputGroup.Addon>}
          </InputGroup>
        )}
        {!showInputGroup && formControl}
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  percentageInput = ({ label, parsedValue, errorDisplay, helperDisplay, placeholder, parsedType, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      hideAddOn = false,
      minimumFractionDigits,
      maximumFractionDigits,
      linkTo,
      fcClassName = "",
      readOnly,
      colProps,
      inArray,
      autoComplete = defaultAutoComplete,
      formGroupClassName = "",
      formGroupStyle = {},
      negative,
      action,
      actionClassName = "",
      actionIcon,
      actionTooltip = "",
      colorSign,
      rightAddOn,
      value,
    } = allProps
    const locale = getLocale()
    const disabled = allProps.disabled ? true : false

    if (parsedValue || parsedValue === 0) {
      if (negative) parsedValue = -parsedValue
      parsedValue = formatPercentage(parsedValue, locale, { minimumFractionDigits, maximumFractionDigits })
      if (!readOnly) parsedValue = parsedValue.replace("%", "").replace(" ", "")
    }

    if (readOnly) {
      const addClassName = colorSign && value < 0 ? "text-danger" : ""
      return this.readOnlyControlInArrayOrInForm({ control: parsedValue ?? "", linkTo, addClassName, helperDisplay, allProps })
    }
    if (parsedValue === 0) parsedValue = "0"

    const inputValue = keyedValue ?? parsedValue ?? ""
    const input = (
      <FormControl
        id={modelFieldPath}
        aria-label={label}
        className={fcClassName}
        type="text"
        bsClass="form-control"
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        autoComplete={autoComplete}
        onChange={this.handleChange}
        onFocus={event => {
          if (inputValue) {
            const decimalSep = getDecimalSymbol(locale)
            const thousandsSep = decimalSep === "," ? "." : ","

            let cleaned = String(inputValue).trim().replace(/\s/g, "")

            if (thousandsSep) cleaned = cleaned.replace(new RegExp(`\\${thousandsSep}`, "g"), "")
            if (decimalSep !== ".") cleaned = cleaned.replace(decimalSep, ".")

            const parsed = Number(cleaned)

            if (!isNaN(parsed)) {
              this.setState({ keyedValue: String(parsed) })
            }
          }
          this.handleFocus(event)
        }}
        onBlur={event => {
          let valueIsNaN = false
          if (inputValue !== "" && inputValue !== null && inputValue !== undefined) {
            const decimalSep = getDecimalSymbol(locale)
            const thousandsSep = decimalSep === "," ? "." : ","

            let cleaned = String(inputValue).trim().replace(/\s/g, "")

            if (thousandsSep) cleaned = cleaned.replace(new RegExp(`\\${thousandsSep}`, "g"), "")
            if (decimalSep !== ".") cleaned = cleaned.replace(decimalSep, ".")

            valueIsNaN = isNaN(Number(cleaned))
          }
          this.handleBlur(event, { isNaN: valueIsNaN, isPercentage: parsedType === "percentage" })
        }}
        onKeyDown={this.handleKeyDown}
        onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
      />
    )

    const formControl =
      hideAddOn && !action ? (
        input
      ) : (
        <InputGroup data-disabled={disabled}>
          {!hideAddOn && <InputGroup.Addon className="form-input-addon">%</InputGroup.Addon>}
          {input}
          {action && (
            <InputGroup.Addon
              className={"form-input-addon c-pointer " + actionClassName}
              onClick={action}
              onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
            >
              <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
            </InputGroup.Addon>
          )}
          {rightAddOn && <InputGroup.Addon>{rightAddOn}</InputGroup.Addon>}
        </InputGroup>
      )

    if (inArray) {
      return (
        <>
          {formControl}
          {helperDisplay}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup
        bsClass={`form-group ${formGroupClassName}`}
        data-disabled={disabled}
        style={formGroupStyle}
        {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
      >
        <ControlLabelWithDebugWithAudit />
        {formControl}
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  currencyInput = ({ label, parsedValue, errorDisplay, helperDisplay, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      style,
      hideAddOn = false,
      minimumFractionDigits,
      maximumFractionDigits,
      linkTo,
      fcClassName = "",
      readOnly,
      placeholder,
      colProps,
      inArray,
      autoComplete = defaultAutoComplete,
      formGroupClassName = "",
      formGroupStyle = {},
      rightAddOn,
      obj,
      currency = obj?.currency,
      symbolClassName = "",
      negative,
      action,
      actionClassName = "",
      actionIcon,
      actionTooltip = "",
      colorSign,
    } = allProps
    const locale = getLocale()
    const disabled = allProps.disabled ? true : false

    if (negative && parsedValue) parsedValue = -parsedValue

    if (readOnly) {
      const addClassName = colorSign && parsedValue < 0 ? "text-danger" : ""
      return this.readOnlyControlInArrayOrInForm({
        control:
          parsedValue || parsedValue === 0
            ? formatCurrency(parsedValue, locale, { currency, minimumFractionDigits, maximumFractionDigits, style })
            : "",
        linkTo,
        addClassName,
        helperDisplay,
        allProps,
      })
    }

    if (parsedValue === 0) parsedValue = "0"

    const _currency = currency || "EUR"
    let _parsedValue = formatCurrency(parsedValue, locale, {
      currency: _currency,
      minimumFractionDigits,
      maximumFractionDigits,
      currencyDisplay: "code",
    })
    if (_parsedValue && typeof _parsedValue === "string") _parsedValue = _parsedValue.replace(_currency, "").trim()
    const inputValue = keyedValue ?? _parsedValue ?? parsedValue ?? ""

    const input = (
      <FormControl
        id={modelFieldPath}
        aria-label={label}
        className={fcClassName}
        type="text"
        inputMode="numeric"
        bsClass="form-control"
        placeholder={placeholder}
        disabled={disabled}
        value={inputValue}
        autoComplete={autoComplete}
        onChange={this.handleChange}
        onBlur={event => {
          this.handleBlur(event, { isNaN: isNaN(unformatCurrency(inputValue, locale)) })
        }}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
      />
    )

    let formControl
    if (hideAddOn && !action) {
      formControl = input
    } else {
      formControl = (
        <InputGroup data-disabled={disabled}>
          {!hideAddOn && (
            <InputGroup.Addon className={"form-input-addon" + symbolClassName}>
              {getCurrencySymbol(locale, currency, { currencyDisplay: getConfigAtPath("currencyDisplay") })}
            </InputGroup.Addon>
          )}
          {input}
          {action && (
            <InputGroup.Addon
              className={"form-input-addon c-pointer " + actionClassName}
              onClick={action}
              onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
            >
              <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
            </InputGroup.Addon>
          )}
          {rightAddOn && <InputGroup.Addon>{rightAddOn}</InputGroup.Addon>}
        </InputGroup>
      )
    }

    if (inArray) {
      return (
        <>
          {formControl}
          {helperDisplay}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup
        bsClass={`form-group ${formGroupClassName}`}
        data-disabled={disabled}
        style={formGroupStyle}
        {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
      >
        <ControlLabelWithDebugWithAudit />
        {formControl}
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )
    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  emailUsernameInput = ({ label, parsedValue, errorDisplay, helperDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      linkTo,
      fcClassName = "",
      readOnly,
      colProps,
      formGroupClassName = "",
      formGroupStyle = {},
      inArray,
      autoComplete = defaultAutoComplete,
    } = allProps
    const disabled = allProps.disabled ? true : false

    if (readOnly) return this.readOnlyControlInArrayOrInForm({ control: parsedValue || "", linkTo, helperDisplay, allProps })

    const inputValue = keyedValue ?? parsedValue ?? ""
    const formInput = (
      <FormGroup
        bsClass={`form-group${inArray ? " mb-0" : ""} ${formGroupClassName}`}
        style={formGroupStyle}
        {...{ [domAttributes.modelFieldPath]: modelFieldPath }}
      >
        {!inArray && <ControlLabelWithDebugWithAudit />}
        <InputGroup data-disabled={disabled}>
          <InputGroup.Addon className="form-input-addon">
            <i className="icn-email-light icn-sm" />
          </InputGroup.Addon>
          {disabled ? (
            <div className="form-control" id={modelFieldPath} disabled>
              {inputValue}
            </div>
          ) : (
            <FormControl
              id={modelFieldPath}
              aria-label={label}
              className={applyClasses({ [fcClassName]: true })}
              type="email"
              autoComplete={autoComplete}
              bsClass="form-control"
              placeholder={placeholder}
              value={inputValue}
              onChange={this.handleChange}
              onKeyDown={this.handleKeyDown}
              onBlur={this.handleBlur}
              onFocus={this.handleFocus}
              onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
              onPaste={event => {
                const pastedValue = (event.clipboardData || window.clipboardData).getData("text")
                const parts = pastedValue?.split(" ")
                if (parts.length <= 1) return

                // This is a small confort code to allow pasting things like "Alain PARFAIT alain.parfait@xxx.com"
                // without having to manually cleanup the value.
                // The first string without space that matches the regex is selected.
                const emailRegex = this.getEmailRegExp()
                for (const part of parts) {
                  if (!part || !emailRegex.test(part)) continue
                  event.preventDefault()
                  this.handleChange({ target: { value: part } })
                  return
                }
              }}
            />
          )}
        </InputGroup>
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  emailsInput = ({ parsedValue, errorDisplay, helperDisplay, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      readOnly,
      colProps,
      formGroupClassName = "",
      formGroupStyle = {},
      select,
      autoComplete = defaultAutoComplete,
      showCopy = false,
    } = allProps
    const disabled = allProps.disabled ? true : false

    let value = keyedValue ?? parsedValue ?? []
    if (Array.isArray(value)) value = value.map(it => ({ value: it, label: it }))
    else if (typeof value === "string" && value) value = [{ value, label: value }]

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        <CreatableSelect
          id={modelFieldPath}
          options={select}
          className="select-group"
          classNamePrefix="select"
          components={getSelectComponents({ isClearable: true, showCopy })}
          formatCreateLabel={text => `${loc`Add`} "${text}"`}
          closeOnSelect={false}
          isMulti={true}
          value={value}
          autoComplete={autoComplete}
          onChange={this.handleChange}
          isDisabled={disabled || readOnly}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          aria-labelledby={modelFieldPath}
        />
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  phoneInput = ({ label, parsedValue, errorDisplay, helperDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete } = this.state
    const {
      field: fieldName,
      inline,
      fcClassName = "",
      readOnly,
      colProps,
      formGroupClassName = "",
      formGroupStyle = {},
      inArray,
      autoComplete = defaultAutoComplete,
      countryCallingCodesList,
      hideClear,
    } = allProps
    const disabled = allProps.disabled ? true : false

    if (readOnly) {
      if (inline) {
        return (
          <Row>
            <Col xs={4} sm={3} md={2} lg={2}>
              <ControlLabelWithDebugWithAudit clClassName="leading-3-5" />
            </Col>
            <Col xs={8} sm={9} md={10} lg={10}>
              <InputGroup data-disabled>
                <InputGroup.Addon className="form-input-addon">
                  <i className="icn-phone icn-xs" />
                </InputGroup.Addon>
                <div className="form-control" id={modelFieldPath} disabled>
                  {parsedValue || ""}
                </div>
              </InputGroup>
            </Col>
          </Row>
        )
      }

      const input = (
        <>
          {!inArray && <ControlLabelWithDebugWithAudit />}
          <InputGroup data-disabled>
            <InputGroup.Addon className="form-input-addon">
              <i className="icn-phone icn-xs" />
            </InputGroup.Addon>
            <div className="form-control" id={modelFieldPath} disabled>
              {parsedValue || ""}
            </div>
          </InputGroup>
        </>
      )
      const formInput = inArray ? (
        input
      ) : (
        <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle}>
          {input}
        </FormGroup>
      )

      return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
    }

    const inputValue = keyedValue ?? parsedValue ?? ""

    const input = (
      <>
        {!inArray && <ControlLabelWithDebugWithAudit />}
        {countryCallingCodesList ? (
          <CountrySelectWrapper
            fieldName={fieldName}
            countryCallingCodesList={countryCallingCodesList}
            modelFieldPath={modelFieldPath}
            value={inputValue}
            label={label}
            className={applyClasses({ [fcClassName]: true })}
            placeholder={placeholder}
            disabled={disabled}
            hideClear={hideClear}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
          />
        ) : (
          <InputGroup data-disabled={disabled}>
            <InputGroup.Addon className="form-input-addon">
              <i className="icn-phone icn-xs" />
            </InputGroup.Addon>
            <FormControl
              id={modelFieldPath}
              aria-label={label}
              className={applyClasses({ [fcClassName]: true })}
              type="tel"
              inputMode="tel"
              pattern=".*[0-9]*"
              bsClass="form-control"
              placeholder={placeholder}
              disabled={disabled}
              value={inputValue}
              autoComplete={autoComplete}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              onFocus={this.handleFocus}
              onKeyDown={this.handleKeyDown}
              onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
            />
          </InputGroup>
        )}
        {helperDisplay}
        {errorDisplay}
      </>
    )

    const formInput = inArray ? (
      input
    ) : (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        {input}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  ibanInput = ({ label, parsedValue, errorDisplay, helperDisplay, placeholder, ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete, debug } = this.state
    const {
      fcClassName = "",
      readOnly,
      colProps,
      formGroupClassName = "",
      formGroupStyle = {},
      action,
      actionClassName = "",
      actionIcon,
      actionTooltip = "",
      autoComplete = defaultAutoComplete,
      inArray,
      help,
      showFormattedIban = false,
    } = allProps

    if (readOnly && !showFormattedIban) return this.readOnlyControlInArrayOrInForm({ control: parsedValue ?? "", helperDisplay, allProps })

    const input = (
      <FormControl
        id={modelFieldPath}
        aria-label={label}
        className={fcClassName}
        type="text"
        bsClass="form-control"
        placeholder={placeholder}
        value={showFormattedIban ? formatIban(keyedValue ?? parsedValue ?? "") : (keyedValue ?? parsedValue ?? "")}
        autoComplete={autoComplete}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
        readOnly={readOnly}
        onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
      />
    )

    const copyButton = showFormattedIban && (
      <InputGroup.Addon
        className={"c-pointer"}
        onClick={() => copyToClipboard(parsedValue)}
        tabIndex="0"
        onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: () => copyToClipboard(unformatIban(parsedValue)) })}
      >
        <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className="icn-copy icn-sm" tooltip={actionTooltip} />
      </InputGroup.Addon>
    )

    const formControl = action ? (
      <InputGroup>
        <InputGroup.Addon
          className={(!showFormattedIban && "form-input-addon ") + "c-pointer " + actionClassName}
          onClick={action}
          onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
        >
          <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
        </InputGroup.Addon>
        {input}
        {copyButton}
      </InputGroup>
    ) : (
      <InputGroup>
        {input}
        {copyButton}
      </InputGroup>
    )

    if (inArray) {
      return (
        <>
          <DebugPopover debug={debug} help={help} modelFieldPath={modelFieldPath} />
          {formControl}
          {helperDisplay}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {formControl}
        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  noticeInput = ({ ControlLabelWithDebugWithAudit, allProps }) => {
    const { colProps, formGroupClassName = "", formGroupStyle = {} } = allProps

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName} notice`} style={formGroupStyle}>
        <ControlLabelWithDebugWithAudit />
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  textPasswordInput = ({
    label,
    parsedValue,
    errorDisplay,
    helperDisplay,
    minMaxDisplay,
    placeholder,
    parsedType,
    ControlLabelWithDebugWithAudit,
    allProps,
  }) => {
    const { modelFieldPath, keyedValue, defaultAutoComplete, debug } = this.state
    const {
      linkTo,
      rightAddOn,
      readOnly,
      colProps,
      fcClassName = "",
      formGroupClassName = "",
      formGroupStyle = {},
      autoComplete = defaultAutoComplete,
      inArray,
      action,
      actionClassName = "",
      actionIcon,
      actionTooltip = "",
      help,
      // Main use case is when the app is in a business-working language but users have to write things in their original spelling.
      // For instance the app might be set to French but the user has to write the names of customers in Arabic.
      rtl,
      name,
      autoFocus,
      inputRef,
      translate,
      showPassword,
    } = allProps
    const disabled = allProps.disabled ? true : false

    if (readOnly && parsedType !== "password") {
      const control = parsedValue?.toString()
      return this.readOnlyControlInArrayOrInForm({ control: translate ? loc(control) : control, linkTo, helperDisplay, translate, allProps })
    }

    const inputValue = keyedValue ?? parsedValue ?? ""
    const control = (
      <FormControl
        id={modelFieldPath}
        aria-label={label}
        inputRef={
          // FormControl only accepts a callback ref. If the caller passes an object ref
          // (from createRef/useRef), wrap it so `.current` still gets the DOM node.
          typeof inputRef === "function" || inputRef == null ? inputRef : node => (inputRef.current = node)
        }
        className={fcClassName + `${rtl ? " direction-rtl" : ""}`}
        type={showPassword ? "text" : parsedType}
        bsClass="form-control"
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        onChange={this.handleChange}
        onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        autoComplete={parsedType === "password" ? autoComplete || "new-password" : autoComplete}
        name={name}
        autoFocus={autoFocus}
      />
    )

    const input = action ? (
      <InputGroup>
        {control}
        <InputGroup.Addon
          className={"form-input-addon c-pointer " + actionClassName}
          onClick={action}
          onKeyDown={event => handleAccessibleOnKeyDown({ event, fn: action })}
        >
          <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={actionIcon || "icn-icons icn-sm"} tooltip={actionTooltip} />
        </InputGroup.Addon>
      </InputGroup>
    ) : rightAddOn ? (
      <InputGroup>
        {control}
        <InputGroup.Addon>{rightAddOn}</InputGroup.Addon>
      </InputGroup>
    ) : (
      control
    )

    if (inArray) {
      return (
        <>
          <DebugPopover debug={debug} help={help} modelFieldPath={modelFieldPath} />
          {input}
          {helperDisplay}
          {minMaxDisplay}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        <ControlLabelWithDebugWithAudit />
        {input}
        {helperDisplay}
        {minMaxDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  colorInput = ({ parsedValue, parsedType, ControlLabelWithDebugWithAudit, errorDisplay, helperDisplay, allProps }) => {
    const { keyedValue, defaultAutoComplete } = this.state
    const { formGroupClassName = "", formGroupStyle, colProps, readOnly, inArray, autoComplete = defaultAutoComplete } = allProps
    const formControlClassName = `form-control${inArray ? " in-array" : ""}`

    const control = parsedValue ? (
      <InputGroup>
        <input
          type={parsedType}
          className={formControlClassName}
          value={keyedValue ?? parsedValue ?? ""}
          autoComplete={autoComplete}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          disabled={readOnly}
        />
        {!readOnly && (
          <InputGroup.Addon className="select-btn c-pointer" onClick={() => this.handleChange({ target: { value: null } })}>
            {/* this svg is the same as the one from react-select */}
            <svg height="19" width="19" viewBox="0 0 19 19">
              <path
                fill="currentColor"
                d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
              ></path>
            </svg>
          </InputGroup.Addon>
        )}
      </InputGroup>
    ) : (
      <div
        className={`${formControlClassName} color-input-no-color`}
        disabled={readOnly}
        onClick={() => !readOnly && this.handleChange({ target: { value: "#000000" } })}
      ></div>
    )

    if (inArray) {
      return (
        <>
          {control}
          {helperDisplay}
          {errorDisplay}
        </>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle}>
        <ControlLabelWithDebugWithAudit />
        {control}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  // These 4 drag handles could be merged with the ones in DocumentComponent
  // or DocumentComponent could use this component with type file.
  handleDragEnter = event => {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter++
    this.setState({ showDropBox: true })
  }

  handleDragLeave = event => {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter--
    if (this.dragCounter <= 0) {
      this.dragCounter = 0
      this.setState({ showDropBox: false })
    }
  }

  handleDragOver = event => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "copy"
  }

  handleDragDrop = (event, { accept, maxDocumentSize, allProps }) => {
    // The accept attribute still allows the user to select any type of file
    // in the select dropdown, so it is unreliable when clicking on the button.
    // When drag and dropping the accept attribute is ignored, so we must validate
    // ourself the types. This is not an easy task, far from perfect, because the format
    // of the declaration is rich https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#unique_file_type_specifiers
    event.preventDefault()
    event.stopPropagation()

    if (accept) {
      const { files } = event.dataTransfer
      event.target.files = files
      // This function calls handleChange so we don't call it here too to not trigger twice pages' render
      this.handleFileControlClick(event, { actionType: actionTypes.SELECT_FILE, accept, maxDocumentSize, allProps })
    } else {
      // kind of hacky
      event.target = event.dataTransfer
      this.handleChange(event)
    }

    this.setState({ showDropBox: false })
  }

  validateUploadFiles = ({ files, accept = "", maxDocumentSize }) => {
    accept = accept.split(",").map(accept => accept.replace(".", "").trim())

    let maxSizeNumber

    if (maxDocumentSize && typeof maxDocumentSize === "string" && maxDocumentSize.toUpperCase().includes("MB")) {
      maxSizeNumber = parseFloat(maxDocumentSize.substring(0, maxDocumentSize.length - 2)) * 1024 * 1024
    }
    const acceptedFiles = []
    for (let i = 0; i < files?.length; i++) {
      const file = files[i]
      const splittedFileName = file.name.split(".") || []
      const fileType = splittedFileName.length > 1 ? splittedFileName.pop().toLowerCase() : file.type

      if (maxSizeNumber && file?.size > maxSizeNumber) {
        file.errorFormat = `${loc("File too large")}. ${loc("Max size")} ${maxDocumentSize}`
      } else if (accept?.[0] && !accept.find(accept => fileType.startsWith(accept) || fileType.endsWith(accept))) {
        file.errorFormat = `${loc("Forbidden document extension")} "${fileType}". ${loc("Accepted formats")}: ${accept}`
      } else {
        delete file.errorFormat
      }
      acceptedFiles.push(file)
    }
    return acceptedFiles
  }

  clickOnFileInput(event, { inputId }) {
    event.stopPropagation()
    document.querySelector(`#${inputId}`)?.click()
  }

  handleFileControlClick = async (event, { value, file, actionType, accept, maxDocumentSize, allProps }) => {
    const _allProps = allProps || this.getAllProps()
    const { action, entityName, entityRegistration, fileType, onSetState, dataToPatchOnFileDelete } = _allProps

    /**
     * This input type is very special because the saving of files is not in the data model.
     * This component handles the saving / delete operations if entityName, entityRegistration and fileType
     * are provided. Otherwise it is up to the scriptor to handle them by using the action props.
     * Cases where the scriptor would decide to handle them is when the file must not be immediately
     * saved / deleted, but rather another user action would be needed, like clicking on a save button.
     */
    const target = event.currentTarget // we need to save a reference of the original target

    if (typeof action === "function") {
      event.actionType = actionType
      event.filename = file?.name
      await action(event)
    }

    if (actionType === actionTypes.VIEW_FILE) {
      if (file._temporaryOfflineDocument) {
        // Queued in IndexedDB while offline, not yet on the server: there is no /content to fetch,
        // so read it back from the local queue (same as DocumentsComponent does). A null result
        // means the user cancelled the passphrase prompt, in which case there is nothing to show.
        const queued = await downloadTemporaryOfflineDocument(file._id)
        if (queued) openDocInTab({ blob: queued.blob, docName: queued.filename })
      } else if (file._id) {
        // The file comes from our platform because it has an _id.
        // The entityName and entityRegistration cannot be passed through props in this case,
        // we rely on what has been saved only.
        let url
        if (file.entityName && file.entityRegistration)
          url = `${getEntityServerRoute(file.entityName)}/${file.entityRegistration}/documents/${file._id}/content`
        else url = `/api/core/documents/${file._id}/content?entityName=Document`

        const { data: blob } = await axios.get(url, { responseType: "blob" })
        openDocInTab({ blob, docName: file.name })
      } else {
        openDocInTab({ base64: await fileToBase64(file, { removeBase64Prefix: false }), docName: file.name })
      }
    }

    if (actionType === actionTypes.DELETE_FILE) {
      if (file._id) {
        if (file._temporaryOfflineDocument) {
          await deleteTemporaryOfflineDocument(file._id)
        } else if (entityName && entityRegistration) {
          await axios.delete(`${getEntityServerRoute(entityName)}/${entityRegistration}/documents/${file._id}`)
        } else {
          const document = (await axios.delete(`/api/core/documents/${file._id}/content`, { data: dataToPatchOnFileDelete })).data
          if (onSetState) onSetState(document)
        }
      }

      if (value) this.handleChange({ target: { files: value.filter(_file => _file.name !== file.name) } })
    }

    if (actionType === actionTypes.SELECT_FILE) {
      this.validateUploadFiles({ files: target.files, accept, maxDocumentSize })

      if (entityName && entityRegistration && fileType) {
        for (let i = 0; i < target.files.length; i++) {
          const file = target.files[i]
          if (file.errorFormat) continue

          const url = `${getEntityServerRoute(entityName)}/${entityRegistration}/documents`
          file.entityName = entityName
          file.entityRegistration = entityRegistration

          if (isOffline() && hasOfflineDocumentUpload()) {
            try {
              const temporaryDocument = await storeTemporaryOfflineDocument({
                url,
                file,
                filename: file.name,
                fields: { type: fileType, name: file.name },
              })
              file._id = temporaryDocument._id
              file._temporaryOfflineDocument = true
            } catch (e) {
              file.errorFormat = e.message
            }
          } else {
            const formData = new FormData()
            formData.append("file", file)
            formData.append("type", fileType)
            formData.append("name", file.name)

            await axios
              .post(url, formData, { headers: { "Content-Type": "multipart/form-data" } })
              .then(({ data }) => (file._id = data._id))
              .catch(e => (file.errorFormat = e.response?.data?.message || e.message))
          }
        }
      }
      this.handleChange({ target })
      target.value = null // this is necessary to allow selecting again a same file
    }

    if (actionType === actionTypes.EDIT_FILE_NAME) {
      this.setState({ showFileNameInput: true, originalFileName: file.name, editableFileName: file.name })
    }
  }

  fileInput = ({ parsedType, parsedValue, errorDisplay, helperDisplay, ControlLabelWithDebugWithAudit, allProps }) => {
    const { keyedValue, modelFieldPath, showDropBox, showFileNameInput, editableFileName, originalFileName } = this.state
    const {
      formGroupClassName = "",
      formGroupStyle,
      colProps,
      field,
      // the default size is small (sm), like for buttons in FormContent
      btnClassName = "btn-sm btn-primary btn-fill",
      multiple: propsMultiple,
      showDelete = true,
      canDelete,
      displayMode,
      deleteText,
      deleteClassName = "text-danger",
      deleteIcon = "icn-xmark",
      deletePosition = "bottom",
      viewText,
      viewClassName = "",
      showView = true,
      viewPosition = "bottom",
      showEdit,
      editText,
      editClassName = "",
      viewIcon = "icn-eye",
      editIcon = "icn-pencil",
      accept,
      btnText,
      iconClassName,
      maxDocumentSize,
      showNoFileHint,
      dropAreaText,
      subText,
      readOnly,
      disabled,
      obj,
    } = allProps

    const multiple = parsedType === "files" ? true : propsMultiple
    const value = keyedValue || (parsedValue ? (Array.isArray(parsedValue) ? parsedValue : [parsedValue]) : undefined)
    const canModify = !readOnly && !disabled

    // Always generate a unique id for the input.
    // Remove non-alphanumeric characters from the modelFieldPath to make sure resulting id is valid.
    const inputIdPrefix = modelFieldPath?.replace(/[^a-zA-Z0-9]/g, "") || field || parsedType
    const inputIdSuffix = obj?.registration || obj?._id || uuidv4()
    const inputId = inputIdPrefix + inputIdSuffix

    // "value[0] instanceof File" indicates that a file has been uploaded in the browser but hasn't been saved yet to the database/objectStorage
    const _canDelete = !readOnly && !disabled && (value?.[0] instanceof File || canDelete)

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle}>
        <ControlLabelWithDebugWithAudit />

        <div
          className={applyClasses({
            "form-input-file-drop-area": true,
            "c-pointer": true,
            "drop-area": displayMode === "drop-area",
          })}
          data-show-drop-area-hint={canModify && showDropBox}
          onDrop={event => canModify && this.handleDragDrop(event, { accept, maxDocumentSize })}
          onDragOver={event => canModify && this.handleDragOver(event)}
          onDragEnter={event => canModify && this.handleDragEnter(event)}
          onDragLeave={event => canModify && this.handleDragLeave(event)}
          onClick={event => canModify && this.clickOnFileInput(event, { inputId, accept })}
        >
          <button
            type="text"
            disabled={!canModify}
            className={"btn " + btnClassName}
            onClick={event => this.clickOnFileInput(event, { inputId, accept })}
          >
            {iconClassName && <i className={iconClassName} />}
            {btnText ? loc(btnText) : multiple ? loc("Files") : loc("File")}
          </button>

          {showView && viewPosition === "left" && (
            <span
              className={"c-pointer ml-10px " + viewClassName}
              onClick={event => {
                event.stopPropagation()
                this.handleFileControlClick(event, { value, file: obj, actionType: actionTypes.VIEW_FILE, accept, allProps })
              }}
            >
              {viewText && <span className="underline">{viewText}</span>}
              {viewIcon && <i className={viewIcon + " icn-xs"} />}
            </span>
          )}

          {_canDelete && showDelete && deletePosition === "left" && (
            <span
              className={"c-pointer ml-10px " + deleteClassName}
              onClick={event => {
                event.stopPropagation()
                this.handleFileControlClick(event, { value, file: obj, actionType: actionTypes.DELETE_FILE, accept })
              }}
            >
              {deleteText && <span className="underline">{deleteText}</span>}
              {deleteIcon && <i className={deleteIcon + " icn-xs"} />}
            </span>
          )}

          {dropAreaText && <div className="form-input-file-drop-area-text mt-5px">{loc(dropAreaText)}</div>}

          <input
            id={inputId}
            type="file"
            accept={accept}
            onChange={event => this.handleFileControlClick(event, { value, actionType: actionTypes.SELECT_FILE, accept, maxDocumentSize, allProps })}
            style={{ hidden: true }}
            multiple={multiple}
          />
        </div>

        {subText && <div className="form-input-subtext">{subText}</div>}

        {value && (
          <div className="form-input-files mt-5px">
            {(value || []).length === 0 && showNoFileHint && <div className="file-input-no-file-hint font-weight-bold mt-5px">{loc("No file")}</div>}

            {!errorDisplay &&
              (showEdit || (showView && viewPosition === "bottom") || (showDelete && deletePosition === "bottom")) &&
              value?.map((file, index) => (
                <div key={index} className="form-input-file mt-5px">
                  {!showFileNameInput && <div className="form-input-filename font-weight-bold">{file.name}</div>}

                  {showFileNameInput && (
                    <FormInputInline
                      field="name"
                      wrapperClassName="font-weight-bold w-100"
                      newValue={getFileNameWithoutExtension(editableFileName)}
                      originalValue={getFileNameWithoutExtension(originalFileName)}
                      updateValueFunction={({ name }) => this.setState({ editableFileName: name })}
                      cancelFunction={event => {
                        if (event) event.stopPropagation()
                        this.setState({ showFileNameInput: false, editableFileName: null, originalFileName: null })
                      }}
                      confirmFunction={event => {
                        if (event) event.stopPropagation()

                        const updateFileName = () =>
                          this.handleChange({
                            target: {
                              files: value.map(_file => {
                                if (_file.name === originalFileName) {
                                  const extension = getFileNameExtension(originalFileName)
                                  const newFileName = `${editableFileName}.${extension}`
                                  return new File([_file], newFileName, { type: _file.type, lastModified: _file.lastModified })
                                }
                                return _file
                              }),
                            },
                          })
                        this.setState({ showFileNameInput: false, originalFileName: null, editableFileName: null }, updateFileName)
                      }}
                    />
                  )}

                  <div className="form-input-file-controls white-space-nowrap">
                    {showEdit && (
                      <span
                        className={"c-pointer ml-10px " + editClassName}
                        onClick={event =>
                          this.handleFileControlClick(event, { value, file, actionType: actionTypes.EDIT_FILE_NAME, accept, allProps })
                        }
                      >
                        {editText && <span className="underline">{editText}</span>}
                        {editIcon && <i className={editIcon + " icn-xs text-success"} />}
                      </span>
                    )}

                    {showView && viewPosition === "bottom" && (
                      <span
                        className={"c-pointer ml-10px " + viewClassName}
                        onClick={event => this.handleFileControlClick(event, { value, file, actionType: actionTypes.VIEW_FILE, accept, allProps })}
                      >
                        {viewText && <span className="underline">{viewText}</span>}
                        {viewIcon && <i className={viewIcon + " icn-xs"} />}
                      </span>
                    )}

                    {showDelete && deletePosition === "bottom" && canModify && (
                      <span
                        className={"c-pointer ml-10px " + deleteClassName}
                        onClick={event => this.handleFileControlClick(event, { value, file, actionType: actionTypes.DELETE_FILE, accept, allProps })}
                      >
                        {deleteText && <span className="underline">{deleteText}</span>}
                        {deleteIcon && <i className={deleteIcon + " icn-xs"} />}
                      </span>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}

        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  sliderInput = ({ ControlLabelWithDebugWithAudit, allProps }) => {
    const { modelFieldPath } = this.state
    const { formGroupClassName = "", formGroupStyle, readOnly, colProps, sliderOptions, disabled } = allProps
    const {
      start = 0,
      step = 10,
      pipsValues = [],
      hidePipsValues,
      tooltips = false,
      pipsMode = "values",
      pipsStepped = true,
      pipsDensity = 100,
      connect = "lower",
      range,
      snap = true,
    } = sliderOptions || {}

    const onSlide = values => this.handleChange({ target: { value: values[0] } })

    const pipsValuesMin = pipsValues[0]
    const pipsValuesMax = pipsValues[pipsValues.length - 1]
    const pipsValuesDiff = pipsValuesMax - pipsValuesMin
    const sliderRange = range ?? {}
    if (!range && step) {
      for (let i = pipsValuesMin; i <= pipsValuesMax; i += step) {
        if (i === pipsValuesMin) {
          sliderRange.min = pipsValuesMin
        } else if (i === pipsValuesMax) {
          sliderRange.max = pipsValuesMax
        } else {
          sliderRange[`${((i - pipsValuesMin) / pipsValuesDiff) * 100}%`] = i
        }
      }
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle}>
        <ControlLabelWithDebugWithAudit />
        <Slider
          id={modelFieldPath}
          start={start}
          step={step}
          connect={connect}
          snap={snap}
          tooltips={tooltips}
          range={sliderRange}
          hidePipsValues={hidePipsValues}
          pips={{
            mode: pipsMode,
            stepped: pipsStepped,
            density: pipsDensity,
            values: pipsValues,
          }}
          onSlide={onSlide}
          disabled={disabled || readOnly}
        />
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  handleMarkdownClick = async event => {
    const zoomedImage = await getZoomedImage(event)
    if (zoomedImage) this.setState({ zoomedImage })
  }

  markdownInput = ({ label, errorDisplay, helperDisplay, placeholder, parsedValue, allProps }) => {
    const {
      keyedValue,
      modelFieldPath,
      renderMarkdown: renderMarkdownState,
      defaultAutoComplete,
      showFormInputValueInModal,
      showRawMarkdown,
      zoomedImage,
    } = this.state
    const {
      renderMarkdown: renderMarkdownProp,
      handleMarkdownRenderToggle,
      fcClassName = "",
      formGroupClassName = "",
      formGroupStyle = {},
      readOnly,
      colProps,
      autoComplete = defaultAutoComplete,
      inArray,
      rows,
      minRows = 1,
      markedInstanceParse = marked,
      showModalButton,
      displayMode,
      mandatory,
      showMandatoryHint = mandatory,
    } = allProps
    const renderMarkdown = renderMarkdownProp ?? renderMarkdownState
    const inputValue = keyedValue ?? parsedValue ?? ""

    let control
    if (readOnly || renderMarkdown) {
      const parseContent = inputValue ? sanitize(markedInstanceParse(inputValue)) : ""
      const mermaidIds = getMermaidIds(parseContent)
      setTimeout(() => execMermaid({ mermaidIds }))
      control = (
        <div
          className={
            "form-input-markdown " +
            (displayMode === "plain" ? "form-input-markdown-plain " : "pd-form-input ") +
            (inArray ? "mb-0 " : "") +
            fcClassName
          }
          // Post-render KaTeX pass; lazily loads the katex chunk only when math is present.
          ref={el => execKatex({ element: el, html: parseContent })}
          dangerouslySetInnerHTML={{ __html: parseContent }}
          onClick={this.handleMarkdownClick}
        />
      )

      if (showModalButton && inputValue) {
        // Here we don't want the className
        const modalControl = (
          <div
            className="form-input-markdown pd-form-input max-h-500px overflow-auto"
            // Post-render KaTeX pass; lazily loads the katex chunk only when math is present.
            ref={el => execKatex({ element: el, html: parseContent })}
            dangerouslySetInnerHTML={{ __html: parseContent }}
            onClick={this.handleMarkdownClick}
          />
        )
        control = (
          <div className="relative">
            <ButtonWithTooltip
              bsStyle="default"
              btnClassName="absolute right-theme"
              className="icn-eye icn-xs c-pointer text-success"
              tooltip="View"
              onClick={() => this.setState({ showFormInputValueInModal: inputValue })}
            />
            {control}

            <Modal
              bsSize="lg"
              show={showFormInputValueInModal}
              onHide={() => this.setState({ showFormInputValueInModal: null })}
              className="table-cell-object-type-value"
            >
              <Modal.Header closeButton>
                <Modal.Title>{loc("Value")}</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <Row className="mb-theme">
                  <Col xs={12}>
                    <CustomButton
                      pullRight
                      fill={showRawMarkdown}
                      bsSize="xsmall"
                      bsStyle="default"
                      onClick={() => this.setState({ showRawMarkdown: !showRawMarkdown })}
                    >
                      {loc`Raw`}
                    </CustomButton>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>{showRawMarkdown ? <pre className="font-1-2 white-space-pre-line max-h-500px">{inputValue}</pre> : modalControl}</Col>
                </Row>
              </Modal.Body>
            </Modal>
          </div>
        )
      }

      if (zoomedImage) {
        control = (
          <>
            {control}
            <ImageZoomModal {...zoomedImage} onHide={() => this.setState({ zoomedImage: null })} />
          </>
        )
      }
    } else {
      control = (
        // this component is almost the same as textarea
        <FormControl
          id={modelFieldPath} // should be unique
          aria-label={label}
          className={fcClassName}
          rows={Math.max(rows || (inputValue ? inputValue.split("\n").length : 0), minRows)}
          componentClass="textarea"
          bsClass="form-control"
          placeholder={placeholder}
          value={inputValue}
          disabled={readOnly}
          autoComplete={autoComplete}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onKeyDown={this.handleKeyDown}
          onInput={({ target }) => {
            if (target.value) {
              // we need to assign twice the height to make the browser auto adjust the height
              target.style.height = "auto"
              target.style.height = target.scrollHeight + 1 + "px"
            } else {
              // let the rows attribute (which honors minRows) and the css min-height define the height
              target.style.height = ""
            }
          }}
          onClick={event => setVirtualKeyboardChangeHandler(event, this.handleChange)}
        />
      )
    }

    const onClick = () => {
      if (renderMarkdownProp !== undefined && handleMarkdownRenderToggle) {
        handleMarkdownRenderToggle()
      } else {
        this.setState({ renderMarkdown: !renderMarkdown })
      }

      setTimeout(() => {
        const formInputElement = document.getElementById(modelFieldPath)
        if (formInputElement) {
          formInputElement.style.height = "auto"
          formInputElement.style.height = formInputElement.scrollHeight + 1 + "px"
        }
      })
    }

    if (inArray) {
      return (
        <InputGroup>
          {!readOnly && (
            <InputGroup.Addon className="form-input-addon c-pointer" onClick={onClick}>
              <ButtonWithTooltip btnClassName="m-0 pd-0" bsStyle="default" className={(renderMarkdown ? "icn-edit" : "icn-eye") + " icn-xs"} />
            </InputGroup.Addon>
          )}

          {control}
        </InputGroup>
      )
    }

    const formInput = (
      <FormGroup bsClass={`form-group ${formGroupClassName}`} style={formGroupStyle} {...{ [domAttributes.modelFieldPath]: modelFieldPath }}>
        {label && (
          <label className="control-label">
            {label}

            {showMandatoryHint && (
              <span className="form-input-mandatory-hint text-danger">
                <i className="icn-asterisk icn-xxxs relative bottom-5px" />
              </span>
            )}

            {!readOnly && (
              <i className={"ml-5px icn-xs max-h-14px c-pointer text-success " + (renderMarkdown ? "icn-edit" : "icn-eye")} onClick={onClick} />
            )}
          </label>
        )}

        {control}

        {helperDisplay}
        {errorDisplay}
      </FormGroup>
    )

    return colProps ? <Col {...colProps}>{formInput}</Col> : formInput
  }

  checkMinMax({ type, value, min, max, obj, currency }) {
    const isText = ["text", "password", "textarea"].includes(type)
    if (isText) value = isString(value) ? value.trim().length : value.length
    const _isLessThanMin = isLessThanMin({ value, min })
    const _isGreaterThanMax = isGreaterThanMax({ value, max })

    if (!_isLessThanMin && !_isGreaterThanMax) return

    const locale = getLocale()
    currency = currency || obj?.currency

    const minOrMax = _isLessThanMin ? min : max

    // In some cases, instanceof Date might not return true even if it is an actual Date object
    const isDate = ["date", "datetime"].includes(type) || minOrMax instanceof Date || Object.prototype.toString.call(minOrMax) === "[object Date]"

    let formattedMinOrMax = minOrMax
    if (type === "currency") formattedMinOrMax = formatCurrency(minOrMax, locale, currency)
    else if (type === "percentage") formattedMinOrMax = formatPercentage(minOrMax, locale)
    else if (isDate) formattedMinOrMax = type === "datetime" ? formatDateTime(minOrMax, locale) : formatDate(minOrMax, locale)

    const symbol = _isLessThanMin ? "≥" : "≤"
    return loc(`${isText ? "Length must" : "Must"} be`) + ` ${symbol} ${formattedMinOrMax}`
  }

  getEmailRegExp() {
    const customEmailRegExp = getOptions("emailValidationRegExp")
    if (customEmailRegExp) return new RegExp(customEmailRegExp)

    return emailRegExp
  }

  getPhoneRegExp({ countryRegExp, callingCode } = {}) {
    // regExp
    if (countryRegExp) {
      return typeof countryRegExp === "string" ? new RegExp(countryRegExp) : countryRegExp
    }

    // default country length
    const nsnLength = getNsnLengthByCallingCode(callingCode)
    if (nsnLength) {
      return getPhoneLengthRegExp(nsnLength, 1)
    }

    // config override
    const customPhoneRegExp = getOptions("phoneValidationRegExp")
    if (customPhoneRegExp) {
      return new RegExp(customPhoneRegExp)
    }

    // default
    return phoneRegExp
  }

  getParsedValue({ value, field, obj }) {
    const { noLodash } = this.props
    let parsedValue
    if (!isEmpty(value)) parsedValue = value
    else if (field) parsedValue = field.includes(".") && !noLodash ? get(obj, field) : obj?.[field]
    return parsedValue
  }

  render() {
    const { field, obj = {} } = this.props
    const allProps = this.getAllProps()
    const {
      value,
      type,
      hidden,
      select,
      mandatory,
      touched,
      warningFormat: propsWarningFormat,
      errorFormat,
      errorHint,
      min,
      max,
      currency,
      helper,
    } = allProps
    const { warningFormat = propsWarningFormat, keyedValue, modelFieldPath } = this.state
    if (hidden) return null

    const parsedValue = this.getParsedValue({ value, field, obj })

    // some comfort code - if we have a date but type is not set to date we will return an object and React will shoot some cabalistic errors.
    let parsedType = type || "text"
    if (!parsedType && parsedValue instanceof Date) parsedType = "date"
    parsedType = parsedType?.toLowerCase()
    if (parsedType === "string") parsedType = "text"

    // We also use keyedValue to avoid displaying an error when starting to write for the first time in an empty input
    const label = this.getLabel(allProps)
    let errorMessage = mandatory && touched && isEmpty(parsedValue) && isEmpty(keyedValue) ? getMandatoryLabel(label) : errorFormat
    let isMinMaxError = false
    if (!errorMessage && !isEmpty(parsedValue) && (!isEmpty(min) || !isEmpty(max))) {
      errorMessage = this.checkMinMax({ type: parsedType, value: parsedValue, min, max, obj, currency })
      if (errorMessage) isMinMaxError = true
    }

    let errorDisplay
    if (errorMessage || warningFormat) {
      const message = errorMessage || warningFormat
      errorDisplay = <InputValidationComponent message={message} isWarning={!errorMessage} errorHint={errorHint} />
    }

    const inputParams = {
      parsedValue,
      parsedType,
      label,
      placeholder: this.getPlaceholder(allProps),
      errorDisplay,
      helperDisplay: getHelperDisplay({ helper }),
      ControlLabelWithDebugWithAudit: params => this.getControlLabelWithDebugWithAudit({ ...params, allProps }),
      allProps,
    }

    if (parsedType === "datetime") return this.datetimeInput(inputParams)
    if (parsedType === "date") return this.dateInput(inputParams)
    if (select && parsedType !== "emails") return this.selectInput(inputParams)
    if (parsedType === "checkbox") return this.checkboxInput(inputParams)
    if (parsedType === "radio") return this.radioInput(inputParams)
    if (parsedType === "switch") return this.switchInput(inputParams)
    if (parsedType === "textarea") {
      return this.textAreaInput({ ...inputParams, minMaxDisplay: getMinMaxDisplay({ value: parsedValue, min, max, isMinMaxError }) })
    }
    if (parsedType === "number" || parsedType === "integer") return this.numberIntegerInput(inputParams)
    if (parsedType === "percentage") return this.percentageInput(inputParams)
    if (parsedType === "currency") return this.currencyInput(inputParams)
    if (["email", "username"].includes(parsedType)) return this.emailUsernameInput(inputParams)
    if (parsedType === "emails") return this.emailsInput(inputParams)
    if (parsedType === "phone") return this.phoneInput(inputParams)
    if (parsedType === "iban") return this.ibanInput(inputParams)
    if (parsedType === "notice") return this.noticeInput(inputParams)
    if (["text", "password"].includes(parsedType)) {
      return this.textPasswordInput({ ...inputParams, minMaxDisplay: getMinMaxDisplay({ value: parsedValue, min, max, isMinMaxError }) })
    }
    if (parsedType === "color") return this.colorInput(inputParams)
    if (["file", "files"].includes(parsedType)) return this.fileInput(inputParams)
    if (parsedType === "slider") return this.sliderInput(inputParams)
    if (parsedType === "markdown") return this.markdownInput(inputParams)
    if (parsedType === "richtext") {
      return (
        <Suspense fallback={null}>
          <FormInputRichText
            {...inputParams}
            getAllProps={this.getAllProps}
            handleChange={this.handleChange}
            handleBlur={this.handleBlur}
            keyedValue={keyedValue}
            modelFieldPath={modelFieldPath}
          />
        </Suspense>
      )
    }

    return <div className="text-danger">{`Unknown type "${parsedType}"`}</div>
  }
}

// When a select or datepicker input is in a table, the opened dropdown or picker may need to overflow the table.
// This is problematic because browsers cannot let an element overflow its parent in one axis while allow scrolling in
// the other axis. This function is a workaround to this problem, applying a data attribute to when dropdown or picker
// is opened. The data attribute is used to clip the y axis when the dropdown or picker is opened.
// BUT, when clipping the x axis, browsers reset all scroll position, including the y-axis one.
// Apply a transform to the BTable to beautifully simulate the resetted scroll position and remove it once the dropdown
// is closed.
export function handleParentTableOverflow({ ref, toggle = true }) {
  const refDropdownIsOpen = ref?.current?.getAttribute(dataDropdownIsOpen)
  // Components call multiple times, but this logic must act as toggle.
  if (refDropdownIsOpen === String(toggle)) return

  const table = ref?.current?.closest?.(".table-component")
  if (table) {
    const bTable = table.querySelector("table.table")
    if (!bTable) return
    const scroll = { x: 0, y: 0 }
    if (toggle) {
      const tableWrapper = table.querySelector(".table-wrapper")
      if (tableWrapper) {
        scroll.x = tableWrapper.scrollLeft || 0
        scroll.y = tableWrapper.scrollTop || 0
      }
    }

    const currentlyOpenedDropdowns = bTable.querySelectorAll(`[${dataDropdownIsOpen}="true"]`)?.length || 0
    // If the user opened another dropdown without closing the previous one, transform is already applied and
    // table-wrapper scroll is 0, so we musn't apply it again, otherwise the scroll position will be messed up.
    if (currentlyOpenedDropdowns + (toggle ? 1 : 0) < 2) bTable.style.transform = `translate(-${scroll.x}px, -${scroll.y}px)`

    ref.current.setAttribute(dataDropdownIsOpen, toggle)
  }
}

function filterOptionFunction(option, typedValue = "") {
  const optVal = removeAllAccents(option.label || option.value)
  if (!optVal) return true
  if (typedValue === null) typedValue = ""

  const typedValues = removeAllAccents(typedValue).replace(/\\/g, "").split(" ")
  for (const val of typedValues) {
    try {
      if (optVal.toString().search(new RegExp(escapeRegExp(val), "i")) === -1) return false
    } catch (error) {
      console.error("FormInput.filterOption.error", error)
      return false
    }
  }
  return true
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export default FormInput

export function getMandatoryLabel(label) {
  if (typeof label === "string" && label) return loc`${label} is mandatory`
  return loc`Field is mandatory`
}

export function updateFieldProps(patch, { obj, field, parsedType, mandatory, label, errorFormat }) {
  const fieldProps = getFieldProps(obj, field)

  let shouldUpdateFieldProps

  // Sync "this.props.mandatory" with "obj.props_{field}.mandatory"
  if (mandatory && !fieldProps.mandatory) {
    fieldProps.mandatory = true
    shouldUpdateFieldProps = true
  }

  // Touch field if not yet touched
  if (!fieldProps.touched) {
    fieldProps.touched = true
    shouldUpdateFieldProps = true
  }

  // Reset field warningFormat
  shouldUpdateFieldProps = shouldUpdateFieldProps || !!fieldProps.warningFormat
  fieldProps.warningFormat = null

  // Set field errorFormat
  if (errorFormat || (!errorFormat && fieldProps.errorFormat)) {
    fieldProps.errorFormat = errorFormat
    shouldUpdateFieldProps = true
  }

  // Set field label error on props
  if (label && parsedType !== "radio") {
    fieldProps.label = label
  }

  if (shouldUpdateFieldProps) setFieldProps(patch, field, fieldProps)
}

export function getSelectComponents({
  isClearable = true,
  showCopy = false,
  hideChevron,
  multiple,
  isLoading,
  handleClear,
  handleNegativeValues,
  selectedValues,
} = {}) {
  const selectComponents = {
    MultiValueRemove: props => {
      const { isDisabled } = props.selectProps || {}
      if (isDisabled) return null

      const { innerProps = {}, data } = props
      // Make each chip's remove "X" keyboard accessible: reachable with Tab and
      // triggerable with Space/Enter. innerProps.onClick is react-select's remove handler.
      const accessibleInnerProps = {
        ...innerProps,
        tabIndex: 0,
        "aria-label": loc`Remove ${data?.label || ""}`,
        onKeyDown: event => {
          if (event.key === " " || event.key === "Enter") {
            event.preventDefault()
            event.stopPropagation()
            innerProps.onClick?.(event)
          }
        },
      }

      return <components.MultiValueRemove {...props} innerProps={accessibleInnerProps} />
    },
    IndicatorsContainer: props => {
      const { isDisabled, getValue } = props
      const selectValues = getValue() || []

      let values, labels
      for (let i = 0; i < selectValues.length; i++) {
        const { label, value } = selectValues[i]
        labels = `${labels ? `${labels}, ` : ""}${label || ""}`
        values = `${values ? `${values}, ` : ""}${value || ""}`
      }

      // The size of these buttons must meet the AA accessibility level
      // meaning their size must be at least 24px x 24px

      // the control of react-select opens the menu on mousedown and cancels the click on touchend
      const stopEvent = event => event.stopPropagation()

      return (
        <components.IndicatorsContainer {...props}>
          {values !== undefined && !isDisabled && showCopy && (
            <ButtonWithTooltip
              className="icn-copy icn-xs select-btn"
              btnClassName="m-0 pd-0 flex-center min-w-24px min-h-24px"
              onClick={() => copyToClipboard(labels || values)}
              onMouseDown={stopEvent}
              onTouchEnd={stopEvent}
            />
          )}

          {isClearable && values !== undefined && !isDisabled && (
            <ButtonWithTooltip
              data-test="empty-input-icon-btn"
              className="icn-xmark icn-xs select-btn"
              btnClassName="m-0 pd-0 flex-center min-w-24px min-h-24px"
              onMouseDown={stopEvent}
              onTouchEnd={stopEvent}
              onClick={event => {
                event?.preventDefault()
                event?.stopPropagation()
                const setValue = handleClear || props.setValue
                setValue(multiple ? [] : null)
              }}
            />
          )}

          {isLoading && <i className="icn-circle-notch icn-xs icn-spin text-gray" />}

          {!hideChevron && !isDisabled && (
            <ButtonWithTooltip
              className="icn-chevron-down icn-xs select-btn"
              btnClassName="m-0 pd-0 flex-center min-w-24px min-h-24px"
              tooltip="Select"
            />
          )}
        </components.IndicatorsContainer>
      )
    },
  }

  if (handleNegativeValues) {
    selectComponents.Option = props => (
      <components.Option {...props}>
        <span data-select-option-value={props.data.value}>{props.data.label}</span>
        <ButtonWithTooltip
          className="icn-minus icn-xxs"
          btnClassName="c-pointer m-0 pdt-0 text-black-lightest min-h-fit"
          tooltip={"Negative filter"}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            props.setValue([...selectedValues, { ...props.data, value: `-${props.data.value}` }])
          }}
        />
      </components.Option>
    )
  }

  return selectComponents
}

/**
 * For now string only, can be evolved into an object helper: { text: "" }
 */
export function getHelperDisplay({ helper }) {
  let helperDisplay = null
  if (typeof helper === "string") {
    helperDisplay = <div className="form-input-helper" dangerouslySetInnerHTML={{ __html: sanitize(marked(helper)) }} />
  }
  if (React.isValidElement(helper)) {
    helperDisplay = <div className="form-input-helper">{helper}</div>
  }
  return helperDisplay
}

export function getMinMaxDisplay({ value, min, max, isMinMaxError = false }) {
  let minMaxDisplay = null
  if (!isEmpty(min) || !isEmpty(max)) {
    if (!isEmpty(value) && isString(value)) value = value.trim()
    const length = !isEmpty(value) ? value.length : 0
    let display = `${length}`
    if (max) display += `/${max}`
    if (min) display += ` (Min: ${min})`
    minMaxDisplay = (
      <div className={`form-input-min-max${isMinMaxError ? " text-danger" : ""}`} dangerouslySetInnerHTML={{ __html: sanitize(marked(display)) }} />
    )
  }
  return minMaxDisplay
}
